// The Scheduling page: the master planning calendar for all job types. A week or month view of
// every dated job (PPM service, call-out, LOLER, remedial), the jobs awaiting office approval, and
// the active projects placed at their planned date. PPM visits are auto-generated from contracts
// (see /api/scheduling/generate-ppm); this page reads them, it never creates PPM jobs by hand.
//
// Admin/office. Dates are handled in UTC throughout to match the backend, so a job always lands in
// the day cell it was scheduled for. Cards are draggable to reschedule (added in the next step).
import { TechOvertimeControl } from "./TechOvertimeControl";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Phone } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import type { CalendarProject, CalendarStandbyWeek, ClearAutoPpmResult, CalendarTrainingSession, DispatchJob, EngineerToldLine, SchedulingCalendar, UnscheduledCallout } from "../../lib/api";
import { isRemedialJobType, jobTypeIcon, jobTypeStyle, jobTypeStyleFor, jobTypeTint } from "../../lib/jobType";
import JobTypeBadge from "../../components/JobTypeBadge";
import { sentenceCaseStatus } from "./CalloutsPage";
import { serviceRecordHref } from "../../lib/serviceRecordLink";
import { useAuth } from "../../lib/auth";
import { initialCoarsePointer } from "../../lib/pointerInput";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { londonWallClockInstant } from "../../lib/londonTime";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { ConfirmButton } from "../../components/ConfirmButton";
import { AccountOnStopLine, AccountSymbol } from "../../components/AccountSymbol";
import { OverflowMenu, overflowItemCls, overflowDangerItemCls } from "../../components/OverflowMenu";
import { Button, ButtonLink, buttonClasses } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { ProposedSlotLine } from "../../components/ProposedSlotLine";
import { PageHeader } from "../../components/PageHeader";
import { StatusPill, toneTextClass } from "../../components/StatusPill";
import { formatDate, formatDateLong, formatDayMonth, formatMonthYear, formatTime, formatWeekdayShort } from "../../lib/format";
import { HeldJobsStrip } from "./HeldJobsStrip";
import { primaryStatePill, statusEntry } from "../../lib/statusRegistry";
import { JOB_HOLD_REASON_OPTIONS, holdLabel, type JobHoldReasonKey } from "../../lib/jobHold";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { BackdatedCompletionModal } from "./BackdatedCompletionModal";
import { CancelJobModal } from "./CancelJobModal";
import { RaiseWorkModal, raisedWorkMessage, type RaiseWorkType } from "./RaiseWorkModal";
import { DayRouteModal } from "./DayRouteModal";
import JobDaysPanel from "./JobDaysPanel";

const DAY_MS = 24 * 60 * 60 * 1000;

// Session AGY adds "day": a single-column card list, the phone-first view (weeks and months
// are min-w-[840px] horizontal scrollers, the ACW-deferred calendar grids).
// Session ARA (S-1) adds "board": the engineer-by-week dispatch board, one row per engineer and one
// column per day of the week, where a drop reschedules AND reassigns in one write. It pages by week
// exactly as the week view does (same cursor, same Prev/Next/Today, same fetch range).
type View = "day" | "week" | "month" | "year" | "board";
type TypeFilter = "all" | "service" | "callout" | "repair" | "inspection" | "safed" | "project" | "pending";

// Only an open job (scheduled / in_progress) can be dragged to a new day; a project, a completed
// visit and a job awaiting approval are fixed. Mirrors the dispatch board's REASSIGNABLE set.
// Session AQY (D-AQX-1): widened to include a paused job (parts_pending) -- it stays draggable,
// reassignable and shows its acceptance state exactly as an ordinary open job does.
const REASSIGNABLE = new Set(["scheduled", "in_progress", "parts_pending"]);

// Drag context passed down to the cells + cards (native HTML5 drag, matching the dispatch board --
// no library, "fewer moving parts"). Week/month grids drop onto a day cell to reschedule; the day
// view drops onto an engineer lane to assign/reassign/unassign instead (Session AJ: this replaces
// the engineer-lane dispatch board, retired in Session O).
interface DragApi {
  // Session ALE: true on a touchscreen, where HTML5 drag cannot fire (see pointerInput.ts).
  //
  // THE RULE, and it is deliberate: this decides what the page SAYS and how big it draws a target.
  // It NEVER decides whether a handler is attached. An iPad reports a coarse PRIMARY pointer even
  // with a Magic Keyboard trackpad attached, and that trackpad drags perfectly well -- which is the
  // device the office is most likely to dispatch from. Removing draggable on a coarse pointer would
  // therefore delete a working capability from the very machine it was meant to help. A gesture that
  // cannot fire costs nothing when left attached; a gesture removed by inference costs a capability.
  // So the cards stay draggable everywhere, and only the wording and the cursor change.
  coarsePointer: boolean;
  movingId: string | null; // a card mid-reschedule (shows a loading state)
  overCell: string | null; // the day cell currently hovered during a drag
  overLane: string | null; // the engineer lane (or "unassigned") currently hovered during a drag, day view only
  onCardDragStart: (jobId: string, workDay?: string | null) => void;
  onCardDragEnd: () => void;
  onCellOver: (cellKey: string) => void;
  onCellLeave: (cellKey: string) => void;
  onDropDay: (day: Date) => void;
  // Drop a dragged job onto an engineer's lane (or the Unassigned lane) in the day view: assigns or
  // reassigns the engineer, day/time untouched. null means the Unassigned lane.
  onLaneOver: (laneKey: string) => void;
  onLaneLeave: (laneKey: string) => void;
  onDropLane: (engineerId: string | null) => void;
  // Session ARA (S-1): drop a dragged job onto one CELL of the engineer-by-week board (an engineer
  // row, or the Unassigned row, crossed with a day column). Unlike onDropDay (day only) and
  // onDropLane (engineer only) this is the one drop that can change BOTH, in a single write, and it
  // sends only what actually differs from the job's current day and engineer. null = the Unassigned
  // row. Hover state reuses overCell / onCellOver / onCellLeave with a composite "lane|day" key, so
  // no second hover mechanism exists; only one view renders at a time, so the keys never collide.
  onDropBoardCell: (engineerId: string | null, day: Date) => void;
  onSelect: (entry: CalEntry) => void; // open the entry detail (click to view / delete)
}

// Session BDG (M2): the Clear auto-PPM confirm's sentence, from the server's own preview counts.
function clearAutoPpmMessage(p: ClearAutoPpmResult | null, includeAssigned: boolean): string {
  const base =
    "Use this after entering the real last-serviced dates on the Lifts page, then click Generate PPM to rebuild the schedule on the corrected dates. Started, awaiting approval, completed and hand-made visits are never touched.";
  if (!p) return base;
  const n = (c: number) => `${c} visit${c === 1 ? "" : "s"}`;
  const removing = p.unassigned + (includeAssigned ? p.assignedNotAccepted : 0);
  return (
    `This would remove ${n(removing)}: unstarted automatic visits ${includeAssigned ? "with nobody on them or with an engineer who has not accepted" : "with nobody on them"}. ` +
    `${n(p.keptAcceptedOrCrewed)} the engineer has accepted or that have a crew are kept either way. ` +
    base
  );
}

// ---- UTC date helpers (mirrors the dispatch board so a card buckets to the same day) ----
function startOfUtcDay(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}
function mondayOf(d: Date): Date {
  const day = startOfUtcDay(d);
  const dow = day.getUTCDay(); // 0=Sun..6=Sat
  const offset = dow === 0 ? -6 : 1 - dow;
  return new Date(day.getTime() + offset * DAY_MS);
}
function firstOfMonth(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}
function addDays(d: Date, n: number): Date {
  return new Date(d.getTime() + n * DAY_MS);
}
function addMonths(d: Date, n: number): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1));
}
function dayKey(iso: string | Date | null): string | null {
  if (!iso) return null;
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
}
function keyOf(d: Date): string {
  return d.toISOString().slice(0, 10);
}
// Session ARA (S-8): parses a `?date=yyyy-mm-dd` deep-link param into the UTC day it names, or null
// for anything absent or malformed -- the caller falls back to today exactly as it always has (the
// same shape as CLAUDE.md's other lenient-parse rules: a bad or missing value degrades gracefully
// rather than throwing).
function parseDateParam(v: string | null): Date | null {
  if (!v || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return null;
  const d = new Date(`${v}T00:00:00.000Z`);
  return Number.isNaN(d.getTime()) ? null : startOfUtcDay(d);
}
// Every calendar day on this page is a UTC-midnight Date, and a UTC midnight is always the same
// calendar day in London (London is UTC+0 or UTC+1, never behind), so the shared London-pinned
// helpers in lib/format.ts print exactly the day the cell is bucketed under. A Date that is NOT a
// UTC midnight (a job's scheduled instant) goes through startOfUtcDay first, so a visit at 00:30
// London (23:30Z the day before) still reads as the day its card sits on, never the next one.
function fmtWeekRange(monday: Date): string {
  const sunday = addDays(monday, 6);
  return `${formatDayMonth(monday)} to ${formatDate(sunday)}`;
}
// Session ARA (S-2): Job.durationMins, compactly -- "45 min" under an hour, "1h" / "1h 30m" at or
// above it. Never negative or fractional in practice (the PPM generator and the raise-work modal both
// write whole minutes), but Math.max/round guard a stray value rather than printing "-5 min" or "1h -3m".
function fmtDuration(mins: number): string {
  const whole = Math.max(0, Math.round(mins));
  if (whole < 60) return `${whole} min`;
  const h = Math.floor(whole / 60);
  const m = whole % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// The 7 days of the week containing `cursor` (Mon..Sun).
function weekDays(cursor: Date): Date[] {
  const monday = mondayOf(cursor);
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}
// The month grid for `cursor`: whole weeks (Mon..Sun) covering the month, 5 or 6 rows.
function monthGridDays(cursor: Date): Date[] {
  const first = firstOfMonth(cursor);
  const daysInMonth = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  const lead = (first.getUTCDay() + 6) % 7; // days before the 1st in its Monday-started week
  const cells = Math.ceil((lead + daysInMonth) / 7) * 7;
  const gridStart = mondayOf(first);
  return Array.from({ length: cells }, (_, i) => addDays(gridStart, i));
}

// The date range to fetch for the current view (covers the full visible grid). The year view
// fetches the whole calendar year (1 Jan to 31 Dec) in this single call -- no new endpoint.
function rangeFor(view: View, cursor: Date): { from: string; to: string } {
  if (view === "year") {
    const y = cursor.getUTCFullYear();
    return {
      from: new Date(Date.UTC(y, 0, 1)).toISOString(),
      to: new Date(Date.UTC(y, 11, 31, 23, 59, 59, 999)).toISOString(),
    };
  }
  // Session ARA (S-1): the board shows the same seven days the week view does, so it fetches the
  // same range. Deliberately not a wider window: the board is a week, and the week feed is what
  // every other week-shaped surface on this page already reads.
  const days = view === "day" ? [startOfUtcDay(cursor)] : view === "week" || view === "board" ? weekDays(cursor) : monthGridDays(cursor);
  const from = days[0]!;
  const lastEnd = new Date(addDays(days[days.length - 1]!, 1).getTime() - 1);
  return { from: from.toISOString(), to: lastEnd.toISOString() };
}

// The reschedule "Notify customer?" modal target (Session AG Item 3).
interface NotifyTarget {
  jobId: string;
  customerName: string | null;
  customerEmail: string | null;
  siteName: string;
  date: Date;
}

// The dispatch "Assign engineer?" branded confirm target (Session AKA). Unlike NotifyTarget above,
// this is shown BEFORE the write: confirming and skipping both call the same reassign endpoint, one
// with notifyNow, so there is exactly one PATCH per assignment and never a stray Gmail draft left
// behind a real send.
interface DispatchConfirmTarget {
  jobId: string;
  engineerId: string;
  engineerLabel: string;
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  // Session SC9B: the customer, so the confirm can carry the account-on-stop line (a flag, never a block).
  customerId?: string | null;
  siteName: string;
  // Session ARA (S-1): the board's combined move carries the new day THROUGH the confirm, so the one
  // PATCH the confirm makes still writes day and engineer together. Absent on the lane-drop and
  // popover paths, which never change the day, so those two write exactly what they always have.
  scheduledFor?: string;
  // Session ARA (S-1): the "Notify customer?" prompt to offer AFTER a confirmed board move of a
  // SERVICE visit whose day changed (the same NotifyModal a day-cell drop offers). Two distinct
  // customer messages, two distinct decisions: "the engineer is on the way" is decided by this
  // confirm BEFORE the write, "your visit has moved" is offered after it. Neither ever auto-sends.
  notifyAfter?: NotifyTarget | null;
}

// ---- A unified calendar entry from a job, a project, or an open call-out ----
//
// Session ALS added the third kind. An OPEN call-out -- one that has never had an engineer -- has no
// Job row at all: the Job that puts a call-out on the calendar and on the engineer PWA is created by
// PATCH /api/callouts/:id/assign, i.e. only once an engineer is chosen. So the one call-out that
// actually needs dispatching could not be represented as a job, and modelling it as one would mean
// pointing the ordinary assign control at an id no job route can resolve. It is its own kind, with
// its own (admin-only) assign route, so the two can never be confused.
interface CalEntry {
  /** Session RC1: a technician visit's overtime agreement, carried from the calendar read. */
  techOvertimeAgreedAt?: string | null;
  techOvertimeAgreedByName?: string | null;
  /** Session PN1: whether the engineer on this job was told, as the server derived it from the audit trail. Jobs only; null when nothing is recorded. */
  engineerTold?: EngineerToldLine | null;
  kind: "job" | "project" | "callout";
  id: string;
  // Session BDM: one WORKING DAY of a multi-day job. The same job (same id, same reference) appears once for each of its days, dated
  // that day; `workDay` is the London day ("YYYY-MM-DD") and `dayLabel` reads "Day 2 of 10". Absent on a one-day job and everything else.
  workDay?: string;
  dayLabel?: string;
  date: string | null;
  ref: string | null; // ELE number, or project ref
  // The canonical {TYPE}-ELE-{number}-{DD}-{MM}-{YY} reference (Session AJF), jobs only. Null for
  // a project (which keeps its own ELE-P-NNNN reference, shown via `ref` above) or when a job
  // has no lift resolved -- `ref` is the fallback display in both cases.
  reference: string | null;
  siteName: string;
  type: string; // job type, or "project"
  status: string; // job status, or project status
  assignedTo: string | null; // engineer id (jobs only)
  autoGenerated: boolean; // PPM auto-generated job (drives the "AUTO" badge); always false for a project
  // Session ACR: the job's asset is care equipment -> the card is labelled "Equipment service"
  // (same locked service colour; a label distinction, never a new colour system).
  equipmentAsset: boolean;
  customerName: string | null; // for the reschedule "Notify customer?" modal (jobs only)
  customerEmail: string | null;
  // For the dispatch "Assign engineer?" branded confirm (Session AKA): whether a text would also go
  // out alongside the "engineer on the way" email.
  customerPhone: string | null;
  // Session AGC: the linked call-out is billed non-contract (walk-in / lapsed) -> the amber
  // "Non-contract" pill. Always false for a project or a service job.
  isNonContract: boolean;
  // Session ALS, undated work only. When this entry has been waiting since (a call-out's reportedAt,
  // a repair's raised date), so the panel can say how long rather than just that it exists. Null for
  // anything with a real date -- the date already says it.
  // Session ALU: whether this visit has a service record on file, straight from the server. Only a
  // job carries one. Undefined means the server did not say (a leaner/older response), which the
  // helpers below treat as "fall back to the status inference", never as "no record".
  hasServiceRecord?: boolean;
  waitingSince?: string | null;
  // An entrapment or an emergency intake: this one is not "when you get to it".
  urgent?: boolean;
  // The fault or work description, shown only in the needs-scheduling panel and the popover, where
  // there is room for it. Never on a day-cell card.
  detail?: string | null;
  // Session ALW: when the ENGINEER confirmed this job, or null when nobody has. UNDEFINED means the
  // server did not say (a leaner or older response), which is deliberately distinct from null.
  acceptedAt?: string | null;
  // Session ALW: a site inspection's own reason, shown in the popover where there is room.
  inspectionReason?: string | null;
  // Session AQW: the CallOut this job IS the dispatch attendance for (a `kind: "job"` entry whose
  // `type` is "callout"), so the record link can open the call-out's own Hub page rather than the
  // job's. Undefined means the server did not say (a leaner/older response); null means the server
  // said this job has no linked call-out (every non-callout job type).
  calloutId?: string | null;
  // Session ARA (S-2): the dispatch-board duration estimate (Job.durationMins), already carried by
  // DispatchJob but never read onto a CalEntry until now, so it never reached a card. Jobs only --
  // undefined for a project or an open call-out, which carry no estimate of their own.
  durationMins?: number | null;
  // Session ARA (S-9): whether this visit currently has an open lone-worker check-in, and whether it
  // is overdue one. Undefined means the server did not say (a leaner/older response), which reads as
  // "nothing to show" rather than a false badge -- the same idiom hasServiceRecord/acceptedAt use.
  loneWorkerCheckedIn?: boolean;
  loneWorkerOverdue?: boolean;
  // Session ASM (Phase 6, D83): the engineers booked besides the primary (jobs only). Undefined = not said.
  crew?: { engineerId: string | null; name: string | null }[];
  // Session ATD (S-8 follow-up): the real customer/site/lift ids behind this entry (jobs only), so
  // the popover can deep-link to them. Null/undefined both mean "no link to show" -- there is
  // nothing this entry distinguishes them for, unlike hasServiceRecord/acceptedAt above.
  customerId?: string | null;
  siteId?: string | null;
  assetId?: string | null;
  // Session SC9A (one look for every state): the facts the card's ONE state pill is chosen from (statusRegistry.ts primaryStatePill).
  // Undefined means the server did not say (a leaner/older response): that state is simply not shown.
  holdReason?: JobHoldReasonKey | null;
  holdReasonText?: string | null;
  entrapment?: boolean;
  slaHeldLabel?: string | null;
  followOnWorks?: boolean;
}

// Session ALW: what the office is told about acceptance, as a small pure decision so the three
// surfaces that show it cannot disagree.
//
//   "pending"  -- somebody has been given this job and has NOT confirmed it. The badge.
//   "accepted" -- confirmed. Shown quietly in the popover; deliberately NOT badged on a card,
//                 because the normal case should not shout.
//   null       -- nothing to say: an unassigned job, a project, an open call-out with no engineer,
//                 a job past scheduling, or a response that did not carry the field at all.
//
// The UNDEFINED case is the one that matters. Treating "the server did not say" as "not accepted"
// would paint a false badge on every card the moment any caller sent a leaner response, which is
// exactly the failure Session ALU recorded for hasServiceRecord and fixed the same way.
export function acceptanceState(entry: CalEntry): "pending" | "accepted" | null {
  if (entry.kind !== "job") return null;
  if (entry.acceptedAt === undefined) return null;
  if (!entry.assignedTo) return null;
  if (!REASSIGNABLE.has(entry.status)) return null;
  return entry.acceptedAt ? "accepted" : "pending";
}

// Session BDM: a job over several working days is the SAME job on each of its days: one card per day, dated that day at the job's own time.
// A one-day job is exactly one card, as it always was.
export function jobToEntries(j: DispatchJob): CalEntry[] {
  const base = jobToEntry(j);
  if (!j.days || j.days.length < 1 || !j.scheduledFor) return [base];
  const time = j.scheduledFor.slice(10); // "THH:MM:SS.sssZ"
  return j.days.map((d) => ({ ...base, date: d.start ?? `${d.day}${time}`, workDay: d.day, dayLabel: d.label }));
}
/** A React key and a lookup key for a card: the job, plus the day for one day of a multi-day job. */
export function entryKey(e: Pick<CalEntry, "kind" | "id" | "workDay">): string {
  return `${e.kind}:${e.id}${e.workDay ? `:${e.workDay}` : ""}`;
}

function jobToEntry(j: DispatchJob): CalEntry {
  return { kind: "job", id: j.id, date: j.scheduledFor, ref: j.eleNumber, reference: j.reference, siteName: j.siteName, type: j.type, status: j.status, assignedTo: j.assignedTo, autoGenerated: j.autoGenerated, equipmentAsset: j.equipmentAsset === true, customerName: j.customerName, customerEmail: j.customerEmail, customerPhone: j.customerPhone, isNonContract: j.isNonContract === true, // Session ALU: carried through verbatim, INCLUDING undefined -- hasRecord() distinguishes "the server said no record" from "the server did not say", and collapsing that to false here would silently drop every link on a response that omits the flag.
    hasServiceRecord: j.hasServiceRecord, // Session ALS: only read for UNDATED work (the needs-scheduling panel). A dated job's own date already says when, and its card has no room for a description.
    waitingSince: j.raisedAt ?? null, detail: j.description ?? null,
    // Session ALW: carried through VERBATIM, including undefined -- see hasServiceRecord's own note
    // above for why. `acceptedAt: undefined` means the server did not say, and acceptanceState()
    // treats that as "unknown, show nothing", never as "not accepted", so an older or leaner
    // response can never paint a false "Not accepted" badge across the whole calendar.
    acceptedAt: j.acceptedAt, inspectionReason: j.inspectionReason ?? null,
    // Session AQW: carried through verbatim, including undefined (see hasServiceRecord's own note).
    calloutId: j.calloutId,
    // Session ARA (S-2): the dispatch-board duration estimate, straight off DispatchJob.
    durationMins: j.durationMins,
    // Session ARA (S-9): carried through verbatim, including undefined -- see the field's own note.
    loneWorkerCheckedIn: j.loneWorkerCheckedIn, loneWorkerOverdue: j.loneWorkerOverdue,
    // Session ASM (Phase 6, D83): carried through verbatim.
    crew: j.crew,
    // Session ATD (S-8 follow-up): the deep-link ids, collapsed to null on an absent field -- see
    // CalEntry's own note for why that is safe here, unlike hasServiceRecord/acceptedAt above.
    customerId: j.customerId ?? null, siteId: j.siteId ?? null, assetId: j.assetId ?? null,
    // Session PN1: whether the engineer on the job was told (push, text, or not told and why), carried through verbatim.
    engineerTold: j.engineerTold ?? null,
    // Session RC1: carried through verbatim (technician visits only).
    techOvertimeAgreedAt: j.techOvertimeAgreedAt, techOvertimeAgreedByName: j.techOvertimeAgreedByName,
    // Session SC9A: the state facts, carried through verbatim.
    holdReason: j.holdReason, holdReasonText: j.holdReasonText, entrapment: j.entrapment, slaHeldLabel: j.slaHeldLabel, followOnWorks: j.followOnWorks };
}
// An open call-out awaiting its first engineer. `date` is deliberately null (a call-out is not
// scheduled for a day -- it is waiting), which keeps it out of entriesByDay and therefore out of
// every day cell; it renders in the "Needs scheduling" panel instead. `reportedAt` rides along as
// `waitingSince` so the panel can say how long it has been sitting.
function calloutToEntry(c: UnscheduledCallout): CalEntry {
  return {
    kind: "callout",
    id: c.id,
    date: null,
    ref: c.eleNumber ?? c.jobRef,
    reference: null,
    siteName: c.siteName || "Site TBC",
    type: "callout",
    status: c.status,
    assignedTo: null,
    autoGenerated: false,
    equipmentAsset: false,
    customerName: c.customerName,
    customerEmail: null,
    customerPhone: null,
    isNonContract: c.isNonContract === true,
    waitingSince: c.reportedAt,
    urgent: c.isEntrapment === true || c.isEmergency === true,
    entrapment: c.isEntrapment === true,
    detail: c.faultDescription,
  };
}
function projectToEntry(p: CalendarProject): CalEntry {
  // A project keeps its own ELE-P-NNNN reference (`ref`, not the job-reference format): a project
  // can span multiple lifts, so there is no single "ELE-{number}" this canonical format could name.
  return { kind: "project", id: p.id, date: p.date, ref: p.ref, reference: null, siteName: p.siteName || p.title, type: "project", status: p.status, assignedTo: null, autoGenerated: false, equipmentAsset: false, customerName: null, customerEmail: null, customerPhone: null, isNonContract: false };
}

// Does this calendar entry have a service record to open, and where? Session ADF established the
// rule inline in the entry popover; Session ALU extracted it here because the DAY VIEW ROW now
// offers the same link, and one rule with two copies is how the pair silently drift apart.
//
// The rule: a visit has a sheet once the ENGINEER has submitted it (pending_approval) or the office
// has approved it (complete). A scheduled or in-progress visit genuinely has none yet, which is a
// fact about the work, not a permission -- so the honest answer is to say so (see
// recordPendingNote below), never to offer a link that lands on a list with nothing to expand.
//
// Two gates that both matter: `kind === "job"` keeps a PROJECT out (it has no service record) and,
// more sharply, keeps an OPEN CALL-OUT out -- a `kind === "callout"` entry's id is a CallOut id,
// not a Job id, so building a ?job= link from it would deep-link to a job that does not exist.
// serviceRecordLink.ts deliberately refuses to own this gate ("it never decides whether to show
// it"), and CalEntry is this page's own type, so this is the one place it can live.
function serviceRecordHrefFor(entry: CalEntry): string | null {
  if (entry.kind !== "job") return null;
  if (!hasRecord(entry)) return null;
  // Session AQW: a call-out-type job passes its OWN call-out id through, so the link opens the
  // call-out's Hub page rather than falling back to the pre-AQW list+query-param convention.
  return serviceRecordHref(entry.id, entry.type, entry.calloutId);
}

// Whether this job has a sheet. The server now SAYS so (hasServiceRecord), and that replaced an
// inference from `status` that was wrong in BOTH directions, each proven against a real write path:
//   - a COMPLETE job can have NO sheet: an office-authored call-out attendance report creates the
//     Job at status "complete" with no JobSheet (server callout.routes.ts), so the old gate offered
//     a link that landed on "that job has not been recorded yet... it will appear once the engineer
//     submits their sheet" -- for a report the office wrote itself, which no engineer will ever
//     submit.
//   - an IN-PROGRESS job can HAVE one: the photo upload endpoint upserts a bare sheet before
//     completion (server job.routes.ts), and the Service Records list filters on archived state and
//     job kind only, never on job status, so that sheet IS listed and deep-links correctly -- while
//     the old gate insisted no record existed.
// The status fallback is kept ONLY for a response that omits the flag, so a leaner or older caller
// behaves exactly as it did before rather than losing every link.
function hasRecord(entry: CalEntry): boolean {
  if (entry.hasServiceRecord != null) return entry.hasServiceRecord;
  return entry.status === "complete" || entry.status === "pending_approval";
}

// The other half of the same rule, in the office's own words. A scheduled or in-progress visit is
// the case the entry popover used to be SILENT about: it showed the delete control (scheduled) or
// a sentence about deletion (in progress) and said nothing at all about the service record, so
// "there is no record yet" was indistinguishable from "the link is missing". The wording matches
// the notice JobSheetList already shows for the same situation reached from the other direction
// (a deep link to a job with no sheet), so the platform tells one story about it.
function recordPendingNote(entry: CalEntry): string | null {
  if (entry.kind !== "job") return null;
  if (hasRecord(entry)) return null;
  // TWO different truths, and saying the wrong one is what this note exists to stop. Work that is
  // still open genuinely has nothing on file yet and the engineer's submission is what creates it.
  // Work that is finished and STILL has no sheet is a different situation entirely (an office-
  // authored call-out attendance report is the real case), and promising the engineer will submit
  // one would be false: nobody is going to.
  if (entry.status === "scheduled" || entry.status === "in_progress") {
    return "This visit has not been recorded yet, so there is no service record to open. It appears once the engineer submits their sheet.";
  }
  return "No service sheet was filed for this visit, so there is no service record to open.";
}

// Card colour + label. Project = blue (ele-loler), a job awaiting approval = amber, otherwise the
// canonical job-type colour from the single shared jobType helper (service green / call-out red /
// LOLER blue / remedial amber; an equipment-asset service keeps the service green but is labelled
// "Equipment service" -- Session ACR). Project + pending reuse real ele/amber tokens that jobType.ts
// already emits, so Tailwind keeps them and the locked per-type scheme is never re-inlined.
function entryStyle(entry: CalEntry): { border: string; text: string; label: string } {
  if (entry.kind === "project") return { border: "border-ele-loler", text: "text-ele-loler", label: "Project" };
  if (entry.status === "pending_approval") {
    const e = statusEntry("job", entry.status);
    return { border: "border-amber-400", text: toneTextClass(e.tone), label: e.label };
  }
  // Session AQY (D-AQX-1): a paused job stays on the calendar (CALENDAR_STATUSES was widened to
  // keep it visible), styled distinctly from its job-type colour so "paused, needs a part" reads at
  // a glance rather than looking like ordinary scheduled work.
  if (entry.status === "parts_pending") {
    const e = statusEntry("job", entry.status);
    return { border: "border-amber-400", text: toneTextClass(e.tone), label: e.label };
  }
  const s = jobTypeStyleFor(entry.type, { equipmentAsset: entry.equipmentAsset });
  return { border: s.border, text: s.text, label: s.label };
}

// ---- Session ARA (S-10): additive state badges, layered on top of entryStyle above -------------
//
// entryStyle's border/text colours are the LOCKED job-type scheme (CLAUDE.md house rules) plus the
// two pre-existing amber overrides (pending_approval, parts_pending) -- none of that is touched here.
// This is a second, independent signal for two states that were confirmed genuinely missing a
// treatment: UNASSIGNED (today only a dim "Unassigned" line under the card, easy to miss, and hidden
// entirely when `compact`) and LATE/overdue (no concept of it existed anywhere on this page). A third
// candidate, NOT ACCEPTED, already has one: EntryCard's own "Not accepted" amber pill
// (acceptanceState(entry) === "pending", below), added in Session ALW before this session -- adding a
// second signal for the identical fact would be clutter, not an additive layer, so it is left alone.
//
// Neither new badge reuses amber (already carrying pending_approval, parts_pending AND the pre-existing
// "Not accepted" pill -- a fourth meaning on one colour is exactly the ambiguity this item was told to
// avoid), and neither reuses a LOCKED job-type colour (red is call-out, which would make an overdue
// call-out's badge read as a second, redundant type signal rather than a new state fact). Both use the
// shared StatusPill this exact card already renders two of, in colours nothing else on
// this card claims: a grey StatusPill (wholly unused elsewhere here) for unassigned, and the
// document red StatusPill (#c0392b, the brand's own "immediate attention" functional colour --
// distinct from the call-out type's --ele-danger token) for overdue.
function isEntryUnassigned(entry: CalEntry): boolean {
  return entry.kind === "job" && !entry.assignedTo && REASSIGNABLE.has(entry.status);
}
// A job is LATE when its own scheduled day has fully passed and it is still open (REASSIGNABLE):
// mirrors the exact `k === todayKey` comparison the calendar's own "today" ring already uses (see
// WeekView/MonthView/the board), never a second date rule. A completed, cancelled or pending-approval
// visit is not late -- it is simply done, whatever its date. Undated work (no CalEntry.date) is never
// late by this rule: there is no day to have passed yet, which is exactly why it lives in the
// Needs-scheduling panel instead of a day cell.
function isEntryLate(entry: CalEntry, todayKey: string): boolean {
  if (entry.kind !== "job") return false;
  if (!REASSIGNABLE.has(entry.status)) return false;
  const k = dayKey(entry.date);
  return k != null && k < todayKey;
}

// The legend above the board: one chip per filterable type (icon from the shared job type helper).
const LEGEND: { key: TypeFilter; label: string; icon: string }[] = [
  { key: "service", label: "Service", icon: "service" },
  { key: "repair", label: "Repair", icon: "repair" },
  { key: "callout", label: "Call-out", icon: "callout" },
  { key: "inspection", label: "Inspection", icon: "loler" },
  // Session BDC: SAFed testing is its own job type, so it has its own chip (its own icon, the blue LOLER token).
  { key: "safed", label: "SAFed", icon: "safed_test" },
];

function matchesType(entry: CalEntry, f: TypeFilter): boolean {
  switch (f) {
    case "all":
      return true;
    case "service":
      return entry.kind === "job" && entry.type === "service";
    case "callout":
      // Session ALS: an OPEN call-out (kind "callout", no Job yet) is still a call-out to the
      // office. Filtering to "Call-outs" and losing the ones that most need dispatching would be
      // exactly backwards.
      return (entry.kind === "job" && entry.type === "callout") || entry.kind === "callout";
    case "repair":
      // Session ALS: repairs became reachable on this page for the first time, so they got a filter
      // of their own. Before this they could not appear at all (born with no date, and the calendar
      // feed is date-bounded), which is why there was nothing to filter.
      //
      // Session ANG folds the TECH VISIT into this option rather than adding an eighth, on the same
      // reading ALW applied to the two observational types one case below: the cards are still
      // labelled separately and the two are still separate job types, but a filter is a view
      // convenience, and an office user narrowing the calendar to remedial work wants both. The
      // option is relabelled "Repair / tech visit" so the dropdown says what it does -- an option
      // reading "Repair" that silently returned tech visits too would be the quieter mistake.
      return entry.kind === "job" && isRemedialJobType(entry.type);
    case "inspection":
      // Session ALW: ONE filter option covers both observational types. They are separate JOB TYPES
      // and are labelled separately on every card -- the Director was explicit that they must not be
      // merged -- but a filter is a view convenience, and an office user narrowing the calendar to
      // "who is out looking at something" wants both. A seventh and eighth option in a dropdown that
      // already has six would cost more than it buys.
      return entry.kind === "job" && (entry.type === "condition_report" || entry.type === "site_inspection" || entry.type === "insurance_check");
    case "safed":
      return entry.kind === "job" && entry.type === "safed_test";
    case "project":
      return entry.kind === "project";
    case "pending":
      return entry.kind === "job" && entry.status === "pending_approval";
  }
}

// The best plain-English sentence an ApiError carries, or the caller's fallback (Session ALS).
//
// `request()` sets ApiError.message to the server's own `message` when it sends one, so a refusal
// that carries a sentence already reads correctly. A refusal that carries only `{ error, details }`
// does not: ApiError.message is then the raw machine code, and the office would be shown
// "invalid_request". That matters here specifically because THIS session added a refusal of exactly
// that shape (the reassign route's "Unknown or inactive engineer" lands in `details.assignedTo`),
// so without this the office's own new error would be the least readable one on the page.
//
// Deliberately not a heuristic on the string: it reads the two places a sentence actually lives,
// and falls back rather than guessing whether a code looks like prose.
export function apiErrorSentence(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  const fromBody = typeof err.body?.message === "string" ? err.body.message.trim() : "";
  if (fromBody) return fromBody;
  const firstDetail = Object.values(err.details ?? {})
    .flat()
    .find((d) => typeof d === "string" && d.trim().length > 0);
  if (firstDetail) return firstDetail;
  // ApiError.message is the server's `message` when there was one, else the raw code. A code has no
  // spaces, which is the one reliable way to tell them apart without inventing a dictionary.
  return err.message && err.message.includes(" ") ? err.message : fallback;
}

// Would assigning `engineerId` (replacing `entry.assignedTo`) raise the "engineer on the way"
// dispatch notification if the office does not intervene? Mirrors shouldNotifyDispatch
// (jobNotifications.ts) plus the service/today gates planDispatchNotification applies server-side,
// so the branded confirm (Session AKA) only interrupts an assignment that could actually notify
// somebody -- an unassign, a same-engineer reassign, a call-out, or a visit not scheduled for today
// goes straight through exactly as before this session. Pure.
function dispatchWouldNotify(entry: CalEntry, engineerId: string | null, todayKey: string): boolean {
  return (
    entry.kind === "job" &&
    entry.type === "service" &&
    Boolean(engineerId) &&
    engineerId !== entry.assignedTo &&
    dayKey(entry.date) === todayKey
  );
}

function matchesEngineer(entry: CalEntry, f: string): boolean {
  if (f === "all") return true;
  if (entry.kind === "project") return false; // projects are not engineer-assigned work
  if (f === "unassigned") return entry.assignedTo == null;
  return entry.assignedTo === f;
}

// The instant a visit dropped onto a calendar day is stored at: 09:00 LONDON time of that date, so it
// is 09:00 whatever the season (Session AQS, S-2; extracted by Session ARA so the day-cell drop, the
// popover date picker and the engineer-by-week board's combined move all compute it HERE and nowhere
// else). `day` is a UTC-midnight anchor of the intended CALENDAR date (startOfUtcDay / Date.UTC
// everywhere on this page), so its own Y/M/D read via getUTC* IS the calendar date; only the time
// needs resolving, through the same londonWallClockInstant the server uses for the equivalent
// problem (server/src/lib/londonTime.ts).
//
// londonWallClockInstant only returns null for a malformed ymd/hhmm, neither of which a fixed
// "09:00" and a well-formed calendar date can ever produce; the UTC fallback is unreachable in
// practice and exists only so this can never persist an Invalid Date.
function scheduledForAtNineLondon(day: Date): string {
  const ymd = `${day.getUTCFullYear()}-${String(day.getUTCMonth() + 1).padStart(2, "0")}-${String(day.getUTCDate()).padStart(2, "0")}`;
  return (londonWallClockInstant(ymd, "09:00") ?? new Date(day.getTime() + 9 * 60 * 60 * 1000)).toISOString();
}

// The page banner after an engineer assignment, composed in ONE place (Session ARA). Every path that
// can put a real engineer on a job (the popover picker, the day-view lane drop, the branded dispatch
// confirm, the open call-out dispatch, and the engineer-by-week board's combined move) reads the
// route's response through this, so a warning the server appends reaches all of them identically
// and none can quietly stop showing one. A warning never turns a success into a failure: the write
// happened, the tone is the platform's amber "worth a second look", and the sentence follows the
// success text rather than replacing it (the Session ANP shape, unchanged).
//
// TWO independent warnings ride here (S-5): the ANP certification match and the ARA same-day
// capacity count. Both are shown in full, in that order, when both are present; neither ever
// replaces the other. The open call-out dispatch route (PATCH /callouts/:id/assign) sends only the
// first today, so its banner is unchanged by construction.
function assignmentNotice(
  baseText: string,
  updated: { certificationWarning?: string | null; capacityWarning?: string | null; leaveWarning?: string | null; travelNotRechecked?: string[] },
): { tone: "success" | "warning"; text: string } {
  // Session BDG (M5): the leave warning (Session ASZ) was computed by the server but never shown here; it rides third.
  // MDF1: a move that left another visit's travel as it was (an approved week, travel changed by hand) is said here too, last.
  const warnings = [updated.certificationWarning, updated.capacityWarning, updated.leaveWarning, ...(updated.travelNotRechecked ?? [])].filter((w): w is string => typeof w === "string" && w.trim().length > 0);
  if (warnings.length === 0) return { tone: "success", text: baseText };
  return { tone: "warning", text: `${baseText} ${warnings.join(" ")}` };
}

// The success sentence for a board move, saying exactly which half (or both) of the move happened.
function boardMoveText(dayChanged: boolean, engineerChanged: boolean, engineerId: string | null): string {
  const engineerText = engineerId ? "engineer assigned" : "engineer unassigned";
  if (dayChanged && engineerChanged) return `Visit moved and ${engineerText}.`;
  if (dayChanged) return "Visit moved.";
  return `${engineerText.charAt(0).toUpperCase()}${engineerText.slice(1)}.`;
}

export function SchedulingPage() {
  const { user } = useAuth();
  // Session ARA (S-8): deep-link params. `?date=yyyy-mm-dd` seeds the INITIAL view date (read once,
  // in the cursor useState initializer below -- see parseDateParam's own note); `?job=<id>` opens
  // that entry's popover once the calendar data has loaded (see the effect near allEntries below).
  // Read-only: this page never writes its own search params back.
  const [searchParams] = useSearchParams();
  // Whether this device can drag at all. Decided ONCE at mount (see pointerInput.ts for why the
  // question is the pointer and not the viewport width). Everything touch-related on this page
  // hangs off this one value, so there is exactly one place to look.
  const [coarsePointer] = useState(initialCoarsePointer);
  // Default view: DAY on a phone AND on any touch device narrower than lg, WEEK everywhere else.
  // Feature-detected on matchMedia and falling back to week where it is absent, so jsdom -- which
  // has no matchMedia -- always renders the week the existing suite pins on load.
  //
  // Session ALE widened this from a bare "(max-width: 767px)". A tablet sits ABOVE that gate (an
  // iPad portrait is 768 to 834 CSS px), so it defaulted to the WEEK grid, which is min-w-[840px]
  // inside a content column of roughly 724 to 738px once the collapsed 64px sidebar is taken off.
  // The tablet therefore opened on a sideways-scrolling grid whose ONLY interaction is drag, which
  // its touchscreen cannot fire: a view you can read and cannot operate. The day view is fluid at
  // any width, so a touch device gets it up to lg, where the week grid genuinely fits again.
  //
  // Deliberately still ONCE AT MOUNT, not a matchMedia subscription: the view is also what the
  // office user picks with the Day/Week/Month/Year buttons, so re-deciding on a viewport change
  // would throw away an explicit choice every time an iPad is rotated. That is the same reasoning
  // initialMobileLayout documents for its own once-at-mount decision.
  const [view, setView] = useState<View>(() => {
    if (typeof window.matchMedia !== "function") return "week";
    if (window.matchMedia("(max-width: 767px)").matches) return "day";
    return initialCoarsePointer() && window.matchMedia("(max-width: 1023px)").matches ? "day" : "week";
  });
  const [cursor, setCursor] = useState<Date>(() => parseDateParam(searchParams.get("date")) ?? startOfUtcDay(new Date()));
  const [data, setData] = useState<SchedulingCalendar | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  // The status banner carries a tone (Session AEH, FIX-2 B3): a success reads green, a failure reads
  // in the danger colour, so an error is never dressed up as a success. Session ANP adds "warning"
  // (amber, the platform's advisory colour): the write still succeeded, but is worth a second look
  // (a certification-match note), never conflated with a failure.
  const [notice, setNotice] = useState<{ tone: "success" | "error" | "warning"; text: string } | null>(null);
  // Session AMF: the "Raise..." control. Null = closed; an object = open, optionally preset to a
  // type (nothing on this page presets one today, but the lift record and the LOLER report page
  // open the SAME modal already knowing what they want, and one modal is the point).
  const [raise, setRaise] = useState<{ type?: RaiseWorkType } | null>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [engineerFilter, setEngineerFilter] = useState<string>("all");
  const [dragJobId, setDragJobId] = useState<string | null>(null);
  // Session BDM: the day being dragged, when the card is one day of a multi-day job (the drop then moves ONLY that day).
  const [dragWorkDay, setDragWorkDay] = useState<string | null>(null);
  const [overCell, setOverCell] = useState<string | null>(null);
  const [overLane, setOverLane] = useState<string | null>(null);
  const [movingJobId, setMovingJobId] = useState<string | null>(null);
  const [bulkAssignBusy, setBulkAssignBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);
  const [clearPreview, setClearPreview] = useState<ClearAutoPpmResult | null>(null);
  const [clearIncludeAssigned, setClearIncludeAssigned] = useState(false);
  // After a SERVICE-visit reschedule, ask whether to email the customer (Session AG Item 3). The
  // reschedule is already saved; this modal only decides whether to send the confirmation email.
  const [notifyTarget, setNotifyTarget] = useState<NotifyTarget | null>(null);
  const [notifySending, setNotifySending] = useState(false);
  // The dispatch "Assign engineer?" branded confirm (Session AKA), shown BEFORE the assignment is
  // written so exactly one reassign call carries the office's notifyNow decision.
  const [dispatchConfirm, setDispatchConfirm] = useState<DispatchConfirmTarget | null>(null);
  // Session ALE: the entry popover reschedule (the only touch route to a new date).
  const [rescheduleBusy, setRescheduleBusy] = useState(false);
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);
  const [dispatchSending, setDispatchSending] = useState(false);
  // The clicked calendar entry (opens a detail + delete popover; Session AR Item 9).
  const [selectedEntry, setSelectedEntry] = useState<CalEntry | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  // Session AQY (D-AQX-1): pause/resume, mirroring the delete busy/error pair above.
  const [pauseResumeBusy, setPauseResumeBusy] = useState(false);
  // Session SC9A: bumped after a hold or a resume so the strip of held jobs reads again.
  const [heldKey, setHeldKey] = useState(0);
  const [pauseResumeError, setPauseResumeError] = useState<string | null>(null);
  // The scheduled visit being recorded as completed on its real past date (Session AEB).
  const [backdatedEntry, setBackdatedEntry] = useState<CalEntry | null>(null);
  // Session AQY (D-AQX-4): the entry the office is cancelling, stashed the same way as
  // backdatedEntry above so the modal survives the detail popover closing.
  const [cancellingEntry, setCancellingEntry] = useState<CalEntry | null>(null);
  // Session ARA (S-14): the day-route panel (map/list of one day's work in visit order).
  const [showDayRoute, setShowDayRoute] = useState(false);

  const range = useMemo(() => rangeFor(view, cursor), [view, cursor]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setData(await api.scheduling.calendar(range.from, range.to));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => {
    void load();
  }, [load]);

  const engineerName = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of data?.engineers ?? []) m.set(e.id, e.name);
    return m;
  }, [data]);

  // Every entry the feed returned, BEFORE the type/engineer filters. Session ALS: assignEngineer
  // resolves the job it is about to write from THIS list, never from the filtered one below. It used
  // to read the filtered list, which produced a false refusal in a real sequence: assign an engineer
  // from the popover while the Engineer filter is set to "Unassigned", the popover deliberately
  // stays open showing the new assignee, and the reload then filters that very job OUT -- so a
  // second change in the same still-open popover was refused with "That visit is no longer on this
  // day", which was not true. A view filter must never decide what may be written.
  const allEntries = useMemo(() => {
    const unscheduled = data?.unscheduled;
    return [
      ...(data?.jobs ?? []).flatMap(jobToEntries),
      // Undated jobs arrive as ordinary DispatchJobs with scheduledFor null, so they go through the
      // SAME mapper and behave as jobs everywhere: entriesByDay skips them (no date), the popover
      // treats them as assignable/reschedulable, and assignEngineer writes them through the one
      // existing route. Session ALW widened the list from repairs alone to every undated type (a
      // repair, a condition report, a site inspection) and renamed the key with it -- and NOTHING
      // here had to change to render the two new ones, which is the point of them being jobs.
      ...(unscheduled?.jobs ?? []).flatMap(jobToEntries),
      ...(unscheduled?.callouts ?? []).map(calloutToEntry),
      ...(data?.projects ?? []).map(projectToEntry),
    ];
  }, [data]);

  // Session ARA (S-8): `?job=<id>` opens that entry's detail popover once the calendar data has
  // loaded. A REF, not state, tracks whether it has already been applied -- once per page load,
  // never re-triggered by an ordinary reload after a write (which would otherwise reopen a popover
  // the office had since closed, every time). If the job is outside the currently-loaded date range
  // (no `?date=` was given, or it names a different week/month), this simply finds nothing and opens
  // nothing: pair it with `?date=` to guarantee the entry is in range.
  const appliedJobParam = useRef(false);
  useEffect(() => {
    if (appliedJobParam.current) return;
    const jobParam = searchParams.get("job");
    if (!jobParam || !data) return;
    appliedJobParam.current = true;
    const found = allEntries.find((e) => e.id === jobParam);
    if (found) {
      setDeleteError(null);
      setSelectedEntry(found);
    }
  }, [searchParams, data, allEntries]);

  // All visible entries, filtered. Drives the calendar itself.
  const entries = useMemo(
    () => allEntries.filter((e) => matchesType(e, typeFilter) && matchesEngineer(e, engineerFilter)),
    [allEntries, typeFilter, engineerFilter],
  );

  // The undated work: everything that can never land in a day cell. Rendered in its own panel above
  // the calendar, on EVERY view (day, week, month, year), because it belongs to no date and a
  // desktop user on the week grid needs it just as much as a phone user on the day view. Filtered
  // like everything else, so the type/engineer filters mean the same thing here as they do above.
  const needsScheduling = useMemo(() => entries.filter((e) => e.date == null), [entries]);

  const entriesByDay = useMemo(() => {
    const m = new Map<string, CalEntry[]>();
    for (const e of entries) {
      const k = dayKey(e.date);
      if (!k) continue;
      const list = m.get(k);
      if (list) list.push(e);
      else m.set(k, [e]);
    }
    return m;
  }, [entries]);

  // Session ARA (S-9): training sessions, grouped by day exactly like entriesByDay above, but kept in
  // a SEPARATE map rather than folded into CalEntry -- a training session is not dispatchable work
  // (no assign route, no drag, no popover), so it never goes through matchesType/matchesEngineer or
  // any of the job/project machinery entriesByDay feeds. Not filtered by the type/engineer controls:
  // those describe job work, and a training session is neither.
  const trainingByDay = useMemo(() => {
    const m = new Map<string, CalendarTrainingSession[]>();
    for (const s of data?.trainingSessions ?? []) {
      const k = dayKey(s.date);
      if (!k) continue;
      const list = m.get(k);
      if (list) list.push(s);
      else m.set(k, [s]);
    }
    return m;
  }, [data]);

  // Session ARA (S-13): computed from the RAW project list (data.projects), never from
  // entries/entriesByDay -- those are already collapsed to CalEntry's own single placement-day
  // shape, and a span needs the two raw dates a filtered, single-day-keyed entry does not carry.
  // Mirrors matchesType/matchesEngineer's OWN treatment of a project entry (never re-deriving them,
  // just applying the same two conditions to the raw list) so the span and the existing single-day
  // marker appear and disappear together: a "callout" type filter, or any specific engineer filter
  // (matchesEngineer excludes every project outright once one is picked, since a project is not
  // engineer-assigned work), hides both rather than leaving an unexplained bar with no card behind it.
  const projSpans = useMemo(() => {
    if (engineerFilter !== "all") return [];
    if (typeFilter !== "all" && typeFilter !== "project") return [];
    return projectSpans(data?.projects ?? []);
  }, [data, typeFilter, engineerFilter]);

  // Per-month entry counts for the cursor's year (year view only). Counted from the filtered
  // entries so the type/engineer filters apply to the year totals too.
  const yearMonthCounts = useMemo(() => {
    const counts = new Array(12).fill(0) as number[];
    if (view !== "year") return counts;
    const year = cursor.getUTCFullYear();
    for (const e of entries) {
      if (!e.date) continue;
      const d = new Date(e.date);
      if (Number.isNaN(d.getTime()) || d.getUTCFullYear() !== year) continue;
      counts[d.getUTCMonth()]!++;
    }
    return counts;
  }, [entries, view, cursor]);

  const todayKey = keyOf(startOfUtcDay(new Date()));
  const visibleDays = useMemo(
    () => (view === "day" ? [startOfUtcDay(cursor)] : view === "week" || view === "board" ? weekDays(cursor) : monthGridDays(cursor)),
    [view, cursor],
  );
  const visibleDayKeys = useMemo(() => new Set(visibleDays.map(keyOf)), [visibleDays]);
  const visibleCount = useMemo(() => {
    let n = 0;
    for (const [k, list] of entriesByDay) if (visibleDayKeys.has(k)) n += list.length;
    return n;
  }, [entriesByDay, visibleDayKeys]);

  // Session ARA (S-1): the board pages by week, exactly as the week view does (same Monday anchor).
  function goPrev() {
    setCursor((c) =>
      view === "day"
        ? addDays(startOfUtcDay(c), -1)
        : view === "week" || view === "board"
          ? addDays(mondayOf(c), -7)
          : view === "month"
            ? addMonths(c, -1)
            : new Date(Date.UTC(c.getUTCFullYear() - 1, 0, 1)),
    );
  }
  function goNext() {
    setCursor((c) =>
      view === "day"
        ? addDays(startOfUtcDay(c), 1)
        : view === "week" || view === "board"
          ? addDays(mondayOf(c), 7)
          : view === "month"
            ? addMonths(c, 1)
            : new Date(Date.UTC(c.getUTCFullYear() + 1, 0, 1)),
    );
  }
  function goToday() {
    setCursor(startOfUtcDay(new Date()));
  }

  // Click a month cell in the year view: open that month in the month view.
  function openMonth(monthIndex: number) {
    setCursor(new Date(Date.UTC(cursor.getUTCFullYear(), monthIndex, 1)));
    setView("month");
  }

  async function generatePpm() {
    setBusy(true);
    setNotice(null);
    try {
      const res = await api.scheduling.generatePpm();
      setNotice({
        tone: "success",
        text:
          res.created > 0
            ? `Generated ${res.created} PPM visit${res.created === 1 ? "" : "s"} from the active contracts.`
            : "No new PPM visits were due to be scheduled.",
      });
      await load();
    } catch {
      setNotice({ tone: "error", text: "Could not generate the PPM schedule. Please try again." });
    } finally {
      setBusy(false);
    }
  }

  // Clear every unstarted auto-generated PPM visit, then the Director clicks Generate PPM to rebuild
  // the schedule on the corrected anchors (after entering the real last-serviced dates per lift).
  // Session BDG (M2): the button first asks the server what a clear would do (counts by kind, nothing deleted), and the
  // branded two-step confirm shows it. A visit the engineer accepted, or that has a crew, is never removed; one with an
  // engineer who has not accepted goes only if the office ticks the box.
  async function openClearAutoPpm() {
    setBusy(true);
    setClearError(null);
    setNotice(null);
    try {
      const preview = await api.scheduling.clearAutoPpm();
      setClearIncludeAssigned(false);
      setClearPreview(preview);
      setConfirmClear(true);
    } catch {
      setNotice({ tone: "error", text: "Could not check what clearing would remove. Nothing has been changed." });
    } finally {
      setBusy(false);
    }
  }

  async function clearAutoPpm() {
    setBusy(true);
    setClearError(null);
    setNotice(null);
    try {
      const res = await api.scheduling.clearAutoPpm({ preview: false, includeAssigned: clearIncludeAssigned });
      setConfirmClear(false);
      const deleted = res.deleted ?? 0;
      setNotice({
        tone: "success",
        text:
          deleted > 0
            ? `Cleared ${deleted} auto-generated PPM visit${deleted === 1 ? "" : "s"}. Click Generate PPM to rebuild the schedule.`
            : "There were no auto-generated PPM visits to clear.",
      });
      await load();
    } catch {
      setClearError("Could not clear the PPM schedule. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  // Drop a dragged job onto a day: reschedule it to 09:00 LONDON TIME of that day (DST-aware) via
  // the hardened reassign endpoint (PATCH /schedule/jobs/:id; single-winner, RLS-scoped,
  // same-day-clash guarded). The card never moves optimistically, so a failed update simply leaves
  // it where it was (the "snap back") and surfaces a notice; on success the reload re-buckets it to
  // the new day.
  // Move a job to 09:00 London time on the given day. Session ALE extracted this out of onDropDay
  // so the desktop DRAG and the entry popover DATE control are two entry points onto ONE
  // implementation: the same endpoint, the same 09:00 normalisation and the same "notify the
  // customer?" follow-up, rather than a second reschedule that could drift from the first. Mirrors
  // how onDropLane and onAssignEngineer already both funnel into api.schedule.reassign and the
  // same dispatch confirm.
  //
  // SESSION AQS, S-2: this used to write `day.getTime() + 9h` -- a flat 09:00 UTC, every day of the
  // year, regardless of season. That is 09:00 London in winter (GMT) but 10:00 London in summer
  // (BST): the office always meant "9am, the working day's start", and the stored instant silently
  // drifted an hour out for seven months of the year. The fix lives in scheduledForAtNineLondon
  // (below the component), which Session ARA extracted so the engineer-by-week board's combined
  // move computes the instant through the SAME function rather than a copy. This is a bug fix in
  // the stored instant only -- no time axis: a day-cell drop still carries no finer time than "the
  // start of the day".
  async function rescheduleJob(jobId: string, day: Date, workDay: string | null = null) {
    // Capture the moved job's customer info BEFORE the reload refreshes the entry list.
    //
    // allEntries, NOT entries, for the same reason assignEngineer reads it (see allEntries). This
    // is the reschedule half of the same defect: with an engineer filter active, a SERVICE visit
    // filtered out of the view resolved to null here, and the "Notify customer?" prompt below is
    // gated on `moved` -- so the office silently skipped being asked whether to email the customer,
    // purely because of what they had the view filtered to. Pre-existing, but it sits one line from
    // the fix and leaving one of the pair reading the filtered list would be arbitrary.
    const moved = allEntries.find((e) => e.kind === "job" && e.id === jobId && (!workDay || e.workDay === workDay)) ?? null;
    const scheduledFor = scheduledForAtNineLondon(day);
    setMovingJobId(jobId);
    setNotice(null);
    try {
      // Session BDM: a card that is one day of a multi-day job moves ONLY that day.
      const updated = await api.schedule.reassign(jobId, { scheduledFor, ...(workDay ? { workDay } : {}) });
      // Session BDG (M5): a day move now carries the capacity and leave warnings for the people on the job. Shown in the
      // same amber banner as an assignment's; a clean move leaves the notice to the caller exactly as before.
      const moveNotice = assignmentNotice("Visit moved.", updated);
      if (moveNotice.tone === "warning") setNotice(moveNotice);
      await load();
      // Reschedule done. For a SERVICE visit, ask whether to email the customer (the email is no
      // longer sent automatically); other job types have no "visit scheduled" email.
      if (moved && moved.type === "service") {
        setNotifyTarget({ jobId, customerName: moved.customerName, customerEmail: moved.customerEmail, siteName: moved.siteName, date: day });
      }
      return true;
    } catch {
      setNotice({ tone: "error", text: "Could not move that job. Please try again." });
      return false;
    } finally {
      setMovingJobId(null);
    }
  }

  async function onDropDay(day: Date) {
    const jobId = dragJobId;
    const workDay = dragWorkDay;
    setDragJobId(null);
    setDragWorkDay(null);
    setOverCell(null);
    if (!jobId) return;
    await rescheduleJob(jobId, day, workDay);
  }

  // Where an assignment was started from. The write and the Session AKA gate are identical either
  // way; only what the office sees afterwards differs (the popover stays open showing the new
  // engineer, a lane drop reports in the page banner).
  type AssignOrigin = "lane" | "popover";

  // THE ONE PLACE assignedTo is written outside the dispatch dialog. Session ALE consolidated the
  // lane drop and the popover picker into this, for two reasons that both bit in the code it
  // replaced. First, they built the AKA gate twice from two DIFFERENT sources of truth: the lane
  // read `entries`, the popover read `selectedEntry` -- which the same function then mutated a few
  // lines later. Second, and worse, BOTH gates were written as `entry && ... && dispatchWouldNotify`,
  // so a job the gate could not resolve FELL THROUGH to the write ungated: the one case where the
  // office most needed asking was the one case it was not asked. This resolves once, from `entries`,
  // and fails CLOSED -- an unresolvable job is never written at all. A new way of assigning cannot
  // route around the confirm, because there is no second route left to route around it on.
  async function assignEngineer(jobId: string, engineerId: string | null, origin: AssignOrigin) {
    // allEntries, NOT entries: a view filter must never decide what may be written. See allEntries.
    const entry = allEntries.find((e) => e.kind === "job" && e.id === jobId) ?? null;
    if (!entry) {
      const msg = "That visit is no longer on this day. Refresh and try again.";
      if (origin === "popover") setAssignError(msg);
      else setNotice({ tone: "error", text: msg });
      return;
    }
    if (engineerId && dispatchWouldNotify(entry, engineerId, todayKey)) {
      // The office has not been asked yet, so nothing is written until they answer.
      if (origin === "popover") {
        setSelectedEntry(null);
        setAssignError(null);
      }
      setDispatchConfirm({
        jobId,
        engineerId,
        engineerLabel: engineerName.get(engineerId) ?? "Engineer",
        customerName: entry.customerName,
        customerEmail: entry.customerEmail,
        customerPhone: entry.customerPhone,
        customerId: entry.customerId ?? null,
        siteName: entry.siteName,
      });
      return;
    }
    if (origin === "popover") setAssignBusy(true);
    else setMovingJobId(jobId);
    setAssignError(null);
    if (origin === "lane") setNotice(null);
    try {
      const updated = await api.schedule.reassign(jobId, { assignedTo: engineerId });
      await load();
      // Keep the popover open showing the new assignee rather than the stale pre-reload snapshot.
      if (origin === "popover") {
        setSelectedEntry((prev) => (prev && prev.kind === "job" && prev.id === jobId ? { ...prev, assignedTo: updated.assignedTo } : prev));
      }
      setNotice(assignmentNotice(engineerId ? "Engineer assigned." : "Engineer unassigned.", updated));
    } catch (err) {
      const msg = apiErrorSentence(err, "Could not assign that engineer. Please try again.");
      if (origin === "popover") setAssignError(msg);
      else setNotice({ tone: "error", text: "Could not assign that engineer. Please try again." });
    } finally {
      if (origin === "popover") setAssignBusy(false);
      else setMovingJobId(null);
    }
  }

  // Session SC5: Accept on a proposed engineer for a REPAIR or TECHNICIAN VISIT. The ordinary reassign route, with the proposal's engineer and, when the job has no
  // date yet, the start of the proposed window as its day. notifyNow is never sent, so nothing reaches the customer (and a repair is not a service visit, so the
  // AKA dispatch confirm never applied to it). The engineer is told, exactly as for any assignment.
  async function acceptProposedJob(jobId: string, engineerId: string, scheduledFor: string | null) {
    setAssignBusy(true);
    setAssignError(null);
    try {
      const updated = await api.schedule.reassign(jobId, { assignedTo: engineerId, ...(scheduledFor ? { scheduledFor } : {}) });
      await load();
      setSelectedEntry(null);
      setNotice(assignmentNotice("Engineer assigned.", updated));
    } catch (err) {
      setAssignError(apiErrorSentence(err, "Could not assign that engineer. Please try again."));
    } finally {
      setAssignBusy(false);
    }
  }

  // Session ASM (Phase 6, Director decision D83): name one more engineer on a visit, or take one off. The
  // same busy/error pair and the same warning banner as assigning the primary, so the office reads one
  // shape of answer whichever engineer they changed.
  async function changeCrew(jobId: string, engineerId: string, action: "add" | "remove") {
    setAssignBusy(true);
    setAssignError(null);
    try {
      const updated = action === "add" ? await api.schedule.addCrew(jobId, engineerId) : await api.schedule.removeCrew(jobId, engineerId);
      await load();
      setSelectedEntry((prev) => (prev && prev.kind === "job" && prev.id === jobId ? { ...prev, crew: updated.crew } : prev));
      setNotice(assignmentNotice(action === "add" ? "Engineer added to the crew." : "Engineer taken off the crew.", updated));
    } catch (err) {
      setAssignError(
        apiErrorSentence(err, action === "add" ? "Could not add that engineer. Please try again." : "Could not take that engineer off. Please try again."),
      );
    } finally {
      setAssignBusy(false);
    }
  }

  // Session AQS, S-3: assign several unassigned visits in the Day view's Unassigned lane to one
  // engineer in a single action, instead of one drag or tap per visit. Deliberately narrower than
  // "bulk assign anything": it operates only on that one lane, where every entry is kind:"job" and
  // already reachable through assignEngineer's own PATCH /schedule/jobs/:id -- an open call-out
  // (Needs scheduling) goes through the separate, admin-only assignCalloutEngineer endpoint and is
  // not offered here. An entry that would trigger the AKA "engineer on the way" notify gate
  // (dispatchWouldNotify: a SERVICE visit, today, a real engineer change) is SKIPPED rather than
  // silently bypassing that confirm -- the office is told how many, and assigns those individually
  // to answer the notify question for each one.
  async function bulkAssignEngineer(jobIds: string[], engineerId: string): Promise<void> {
    const gated = jobIds.filter((id) => {
      const entry = allEntries.find((e) => e.kind === "job" && e.id === id);
      return entry != null && dispatchWouldNotify(entry, engineerId, todayKey);
    });
    const toAssign = jobIds.filter((id) => !gated.includes(id));
    setBulkAssignBusy(true);
    setNotice(null);
    let succeeded = 0;
    let failed = 0;
    // Session RC1: the first refusal's own sentence (for example "Only an engineer marked as a technician can be booked on a technician visit.").
    let firstRefusal: string | null = null;
    for (const jobId of toAssign) {
      try {
        await api.schedule.reassign(jobId, { assignedTo: engineerId });
        succeeded++;
      } catch (err) {
        failed++;
        firstRefusal ??= apiErrorSentence(err, "");
      }
    }
    await load();
    setBulkAssignBusy(false);
    const engLabel = engineerName.get(engineerId) ?? "the engineer";
    const parts: string[] = [];
    if (succeeded > 0) parts.push(`${succeeded} visit${succeeded === 1 ? "" : "s"} assigned to ${engLabel}.`);
    if (failed > 0) parts.push(`${failed} could not be assigned.${firstRefusal ? ` ${firstRefusal}` : ""}`);
    if (gated.length > 0) {
      parts.push(`${gated.length} skipped: assign ${gated.length === 1 ? "it" : "them"} individually to confirm whether to notify the customer.`);
    }
    setNotice({ tone: failed > 0 || gated.length > 0 ? "warning" : "success", text: parts.join(" ") || "Nothing to assign." });
  }

  // Give an OPEN call-out its first engineer (Session ALS). A genuinely different write from
  // assignEngineer above, and deliberately not folded into it:
  //
  //   * a different endpoint. PATCH /api/callouts/:id/assign, which CREATES the dispatch job (dated
  //     to now) as well as recording the engineer on the call-out. There is no job to PATCH yet, so
  //     the ordinary reassign route would 404 on this id.
  //   * a different permission. That route is ADMIN ONLY (callout.routes.ts adminGuard), where the
  //     job reassign is admin AND office. The popover therefore does not offer an office user a
  //     dropdown here at all -- it says why -- rather than presenting a control whose every use
  //     would be refused.
  //   * no dispatch confirm. The Session AKA "engineer on the way" gate is a SERVICE-visit rule
  //     (dispatchWouldNotify requires type "service"), and the call-out pipeline sends its own
  //     customer confirmation on its own terms. Nothing new is emailed by this write.
  async function assignCalloutEngineer(calloutId: string, engineerId: string) {
    setAssignBusy(true);
    setAssignError(null);
    try {
      const updated = await api.callouts.assign(calloutId, engineerId);
      await load();
      // The call-out has now become a real dated job, so this entry no longer exists in the shape
      // the popover is rendering. Close it and report in the page banner rather than leaving a stale
      // panel open over a record that has changed underneath it.
      setSelectedEntry(null);
      setNotice(assignmentNotice("Engineer assigned. The call-out is now on today's schedule.", updated));
    } catch (err) {
      setAssignError(apiErrorSentence(err, "Could not assign that engineer. Please try again."));
    } finally {
      setAssignBusy(false);
    }
  }

  // Drop a dragged job onto an engineer's lane (or the Unassigned lane) in the day view: assigns or
  // reassigns the engineer, day/time untouched. Mirrors onDropDay above but writes assignedTo
  // instead of scheduledFor, through the shared assignEngineer.
  async function onDropLane(engineerId: string | null) {
    const jobId = dragJobId;
    setDragJobId(null);
    setOverLane(null);
    if (!jobId) return;
    await assignEngineer(jobId, engineerId, "lane");
  }

  // ---- Session ARA (S-1): the engineer-by-week board's ONE combined write ----------------------
  //
  // Dropping a job onto a (engineer row, day column) cell can change the day, the engineer, or
  // both. It is deliberately its OWN function rather than an overload of rescheduleJob (day only)
  // or assignEngineer (engineer only): wiring the board through either would silently drop the
  // other half of the move, and chaining the two would be two PATCHes for one gesture, with the
  // AKA gate and the reload each firing twice. Everything it shares with them, it shares by
  // CALLING the same thing:
  //   - the stored instant is scheduledForAtNineLondon, the identical function rescheduleJob uses;
  //   - the endpoint is the same PATCH /schedule/jobs/:id, sending ONLY the field(s) that actually
  //     differ from the job's current day and engineer (the existing "only what changed" pattern);
  //   - the "engineer on the way" gate is the same dispatchWouldNotify, handed off to the same
  //     branded DispatchConfirmModal through setDispatchConfirm, evaluated on the POST-MOVE date
  //     (the server decides "today" from the scheduledFor it has just written, so a visit dragged
  //     from Friday onto today's cell for a new engineer CAN notify, and must be asked about);
  //   - the "Notify customer?" follow-up for a SERVICE visit whose day changed is the same
  //     NotifyModal a day-cell drop offers, through the same setNotifyTarget;
  //   - the banner is the same assignmentNotice composition every other assign path uses.
  // It resolves the job from allEntries and fails CLOSED when it cannot (the ALE rule: a job the
  // gate cannot resolve is never written ungated).
  //
  // A card dropped onto the cell it already occupies writes nothing at all.
  async function moveJobOnBoard(jobId: string, engineerId: string | null, day: Date, workDay: string | null = null): Promise<void> {
    const entry = allEntries.find((e) => e.kind === "job" && e.id === jobId && (!workDay || e.workDay === workDay)) ?? null;
    if (!entry) {
      setNotice({ tone: "error", text: "That visit is no longer on the board. Refresh and try again." });
      return;
    }
    const dayChanged = dayKey(entry.date) !== keyOf(day);
    const engineerChanged = (entry.assignedTo ?? null) !== engineerId;
    if (!dayChanged && !engineerChanged) return;
    const body: { scheduledFor?: string; assignedTo?: string | null; workDay?: string } = {};
    if (dayChanged) {
      body.scheduledFor = scheduledForAtNineLondon(day);
      if (workDay) body.workDay = workDay;
    }
    if (engineerChanged) body.assignedTo = engineerId;
    const notifyAfter: NotifyTarget | null =
      dayChanged && entry.type === "service"
        ? { jobId, customerName: entry.customerName, customerEmail: entry.customerEmail, siteName: entry.siteName, date: day }
        : null;
    // The gate reads the entry AS IT WILL BE after the move: same engineer test, same "is it a
    // service visit" test, but the day is the target cell's, never the day it is being dragged from.
    if (engineerId && dispatchWouldNotify({ ...entry, date: day.toISOString() }, engineerId, todayKey)) {
      setDispatchConfirm({
        jobId,
        engineerId,
        engineerLabel: engineerName.get(engineerId) ?? "Engineer",
        customerName: entry.customerName,
        customerEmail: entry.customerEmail,
        customerPhone: entry.customerPhone,
        customerId: entry.customerId ?? null,
        siteName: entry.siteName,
        scheduledFor: body.scheduledFor,
        notifyAfter,
      });
      return;
    }
    setMovingJobId(jobId);
    setNotice(null);
    try {
      const updated = await api.schedule.reassign(jobId, body);
      await load();
      setNotice(assignmentNotice(boardMoveText(dayChanged, engineerChanged, engineerId), updated));
      if (notifyAfter) setNotifyTarget(notifyAfter);
    } catch (err) {
      setNotice({ tone: "error", text: apiErrorSentence(err, "Could not move that visit. Please try again.") });
    } finally {
      setMovingJobId(null);
    }
  }

  async function onDropBoardCell(engineerId: string | null, day: Date) {
    const jobId = dragJobId;
    const workDay = dragWorkDay;
    setDragJobId(null);
    setDragWorkDay(null);
    setOverCell(null);
    if (!jobId) return;
    await moveJobOnBoard(jobId, engineerId, day, workDay);
  }

  // Resolve the "Assign engineer?" branded confirm: exactly ONE reassign call either way, carrying
  // the office's notifyNow decision, so a confirmed real send can never leave a stray Gmail draft of
  // the same message sitting in Drafts behind it. Declining/dismissing calls the identical endpoint
  // without notifyNow, which is exactly the pre-AKA default: the assignment is made and the AJI
  // draft is raised for later review.
  //
  // Session ARA (S-1): when the confirm was raised by a board move that also changed the day, the
  // target carries scheduledFor, and it rides in the SAME single PATCH; the lane-drop and popover
  // paths never set it, so their write is unchanged. notifyAfter, likewise only ever set by the
  // board, offers the "Notify customer?" prompt once the write has landed.
  async function confirmDispatch(notifyNow: boolean) {
    if (!dispatchConfirm) return;
    const { jobId, engineerId, scheduledFor, notifyAfter } = dispatchConfirm;
    setDispatchSending(true);
    setNotice(null);
    try {
      const updated = await api.schedule.reassign(jobId, {
        assignedTo: engineerId,
        ...(scheduledFor ? { scheduledFor } : {}),
        ...(notifyNow ? { notifyNow: true } : {}),
      });
      await load();
      const baseText = notifyNow ? "Engineer assigned, and the customer has been notified." : "Engineer assigned.";
      setNotice(assignmentNotice(scheduledFor ? `Visit moved. ${baseText}` : baseText, updated));
      if (notifyAfter) setNotifyTarget(notifyAfter);
    } catch {
      setNotice({ tone: "error", text: "Could not assign that engineer. Please try again." });
    } finally {
      setDispatchSending(false);
      setDispatchConfirm(null);
    }
  }

  // Send the confirmation email the office just chose to send; the reschedule is already saved.
  async function confirmNotify() {
    if (!notifyTarget) return;
    setNotifySending(true);
    try {
      await api.jobs.notifyScheduled(notifyTarget.jobId);
      setNotice({ tone: "success", text: "Confirmation email sent to the customer." });
    } catch {
      setNotice({ tone: "error", text: "Could not send the confirmation email. The visit is still rescheduled." });
    } finally {
      setNotifySending(false);
      setNotifyTarget(null);
    }
  }

  // Reschedule from the entry popover. Calls the SAME rescheduleJob the day-cell drag calls, so
  // the payload, the 09:00 UTC normalisation and the "notify the customer?" follow-up are shared,
  // never reimplemented. On success the popover CLOSES: the visit has moved to a different day, so
  // a popover still sitting over the old day is stale, and closing it also clears the way for the
  // notify modal rather than stacking two dialogs.
  async function onReschedulePopover(jobId: string, day: Date, workDay: string | null = null) {
    setRescheduleBusy(true);
    setRescheduleError(null);
    try {
      const ok = await rescheduleJob(jobId, day, workDay);
      if (ok) {
        setSelectedEntry(null);
        // Keep a capacity or leave warning rescheduleJob just raised; otherwise the plain success line, as before.
        setNotice((cur) => (cur?.tone === "warning" ? cur : { tone: "success", text: "Visit moved." }));
      } else {
        setRescheduleError("Could not move that visit. Please try again.");
      }
    } finally {
      setRescheduleBusy(false);
    }
  }

  const drag: DragApi = {
    coarsePointer,
    movingId: movingJobId,
    overCell,
    overLane,
    onCardDragStart: (jobId, workDay) => {
      setDragJobId(jobId);
      setDragWorkDay(workDay ?? null);
    },
    // Clear ALL hover/drag state on drag end so a cancelled drag (Escape, or release over a
    // non-droppable region) never leaves a day cell or engineer lane highlighted with no active drag.
    onCardDragEnd: () => {
      setDragJobId(null);
      setDragWorkDay(null);
      setOverCell(null);
      setOverLane(null);
    },
    onCellOver: setOverCell,
    onCellLeave: (k) => setOverCell((c) => (c === k ? null : c)),
    onDropDay,
    onLaneOver: setOverLane,
    onLaneLeave: (k) => setOverLane((c) => (c === k ? null : c)),
    onDropLane,
    onDropBoardCell,
    onSelect: (entry) => {
      setDeleteError(null);
      setSelectedEntry(entry);
    },
  };

  // Delete the selected scheduled visit. Only an unstarted SERVICE/other job (status "scheduled",
  // no job sheet) is deletable; the server enforces this and returns a 409 with a clear message
  // otherwise, which we surface in the popover.
  async function deleteSelected() {
    if (!selectedEntry || selectedEntry.kind !== "job") return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.schedule.deleteJob(selectedEntry.id);
      setSelectedEntry(null);
      await load();
      setNotice({ tone: "success", text: "The scheduled visit was deleted." });
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message || err.code : "Could not delete the visit. Please try again.");
    } finally {
      setDeleteBusy(false);
    }
  }

  // Assign (or unassign) an engineer on the selected job. This is the fix for the gap left when the
  // engineer-lane dispatch board was retired (Session O): SchedulingPage took over rescheduling but
  // never grew an assign control of its own, so no routed office page has sent assignedTo since. The
  // backend already accepts assignedTo alone (see scheduling.routes.ts), so this only wires the client.
  // Session AKA: when the pick would raise the dispatch notification, the branded confirm decides
  // first -- the entry popover hands off to it exactly as it already does for "Record as completed".
  async function onAssignEngineer(jobId: string, engineerId: string | null) {
    await assignEngineer(jobId, engineerId, "popover");
  }

  // Pause / resume a job (Session AQY, D-AQX-1). Works for any job type; refetches the calendar so
  // the card's status badge and colour update without a manual reload.
  async function pauseJob(jobId: string, holdReason: JobHoldReasonKey, reasonText: string) {
    setPauseResumeBusy(true);
    setPauseResumeError(null);
    try {
      await api.schedule.pause(jobId, holdReason, reasonText);
      setSelectedEntry(null);
      await load();
      setHeldKey((k) => k + 1);
      setNotice({ tone: "success", text: `The visit is ${holdLabel(holdReason, reasonText).toLowerCase()}.` });
    } catch (err) {
      setPauseResumeError(err instanceof ApiError ? err.message || err.code : "Could not pause the visit. Please try again.");
    } finally {
      setPauseResumeBusy(false);
    }
  }

  async function resumeJob(jobId: string) {
    setPauseResumeBusy(true);
    setPauseResumeError(null);
    try {
      await api.schedule.resume(jobId);
      setSelectedEntry(null);
      await load();
      setHeldKey((k) => k + 1);
      setNotice({ tone: "success", text: "The visit was resumed." });
    } catch (err) {
      setPauseResumeError(err instanceof ApiError ? err.message || err.code : "Could not resume the visit. Please try again.");
    } finally {
      setPauseResumeBusy(false);
    }
  }

  return (
    <div>
      <PageHeader
        className="mb-3"
        title="Scheduling"
        subtitle={
          view === "day"
            ? formatDateLong(cursor)
            : view === "week" || view === "board"
              ? `Week of ${fmtWeekRange(mondayOf(cursor))}`
              : view === "month"
                ? formatMonthYear(cursor)
                : String(cursor.getUTCFullYear())
        }
      />
      {/* The controls sit in their own row under the header rather than in PageHeader's actions slot:
          there are ten of them, and that slot does not shrink, so beside the title they would push the
          page into a horizontal scroll between the sm and xl widths where the old header wrapped them.
          ele-touch-row keeps the 44px floor on a coarse pointer. */}
      <div className="ele-touch-row mb-4 flex flex-wrap items-center gap-2">
        {/* Week / Month pill toggle */}
        {/* max-w-full flex-wrap (Session BDY): five buttons are wider than a 360px phone at a 1.3x system text size, so "Year" was cut at the screen edge; they now wrap onto a second row instead. */}
        <div className="inline-flex max-w-full flex-wrap overflow-hidden rounded-ele border border-ele-purple-accent" role="group" aria-label="Calendar view">
          {/* Session ARA (S-1): the engineer-by-week board. First in the row because it is the
              dispatch surface (engineer x day); the page's DEFAULT view is unchanged, deliberately,
              since that is a product decision with its own pin (the desktop-defaults-to-week test),
              not a side effect of adding an option. */}
          <button
            onClick={() => setView("board")}
            aria-pressed={view === "board"}
            className={`max-sm:min-h-11 px-3 py-2 font-menu text-xs tracking-wider ${view === "board" ? "bg-ele-purple text-white" : "text-ele-body hover:text-ele-strong"}`}
          >
            Board
          </button>
          <button
            onClick={() => setView("day")}
            aria-pressed={view === "day"}
            className={`max-sm:min-h-11 px-3 py-2 font-menu text-xs tracking-wider ${view === "day" ? "bg-ele-purple text-white" : "text-ele-body hover:text-ele-strong"}`}
          >
            Day
          </button>
          <button
            onClick={() => setView("week")}
            aria-pressed={view === "week"}
            className={`max-sm:min-h-11 px-3 py-2 font-menu text-xs tracking-wider ${view === "week" ? "bg-ele-purple text-white" : "text-ele-body hover:text-ele-strong"}`}
          >
            Week
          </button>
          <button
            onClick={() => setView("month")}
            aria-pressed={view === "month"}
            className={`max-sm:min-h-11 px-3 py-2 font-menu text-xs tracking-wider ${view === "month" ? "bg-ele-purple text-white" : "text-ele-body hover:text-ele-strong"}`}
          >
            Month
          </button>
          <button
            onClick={() => setView("year")}
            aria-pressed={view === "year"}
            className={`max-sm:min-h-11 px-3 py-2 font-menu text-xs tracking-wider ${view === "year" ? "bg-ele-purple text-white" : "text-ele-body hover:text-ele-strong"}`}
          >
            Year
          </button>
        </div>
        <button onClick={goPrev} aria-label="Previous" className="max-sm:min-h-11 rounded-ele border border-ele-purple-accent px-3 py-2 text-sm hover:border-ele-green">
          &larr; Prev
        </button>
        <button onClick={goNext} aria-label="Next" className="max-sm:min-h-11 rounded-ele border border-ele-purple-accent px-3 py-2 text-sm hover:border-ele-green">
          Next &rarr;
        </button>
        <button onClick={goToday} className="max-sm:min-h-11 rounded-ele border border-ele-purple-accent px-3 py-2 text-sm hover:border-ele-green">
          Today
        </button>
        <ButtonLink to="/office/callouts" icon={<Phone aria-hidden size={15} />}>
          New call-out
        </ButtonLink>
        {/* SESSION SC7B (the calm screens, part 2): ONE primary action (New call-out), the view toggles and Prev / Next / Today
            stay in the row; the four secondary actions sit in the shared overflow menu. Nothing was removed.
              - Day route (Session ARA, S-14): a map/list of one day's dispatched work in visit order, with a rough drive
                time. Its own small self-contained panel (DayRouteModal), not a new `view` mode, so it never touches the
                day/week/month/board render branches below.
              - Raise... (Session AMF): a planned repair, a Full Condition Report or a site inspection -- the three types
                that are born with no date and no engineer, and until AMF could be raised only from chat.
              - Generate PPM (D57, Session ARA): operational, not financial, so it opens to office alongside admin.
              - Clear auto-PPM (Session AQS, S-18; BDG M2): deletes every unstarted auto-generated PPM visit, so it is set
                apart in the danger style and still goes through its branded confirm (openClearAutoPpm -> the
                ConfirmDeleteModal below, whose preview counts the visits first). */}
        <OverflowMenu label="More scheduling actions" closeOnSelect>
          <button type="button" role="menuitem" onClick={() => setShowDayRoute(true)} className={overflowItemCls}>
            Day route
          </button>
          <button type="button" role="menuitem" onClick={() => setRaise({})} className={overflowItemCls}>
            Raise...
          </button>
          {(user?.role === "admin" || user?.role === "office") && (
            <>
              <button type="button" role="menuitem" onClick={generatePpm} disabled={busy} className={`${overflowItemCls} disabled:opacity-50`}>
                Generate PPM
              </button>
              <button type="button" role="menuitem" onClick={() => void openClearAutoPpm()} disabled={busy} className={`${overflowDangerItemCls} disabled:opacity-50`}>
                Clear auto-PPM
              </button>
            </>
          )}
        </OverflowMenu>
      </div>

      <ConfirmDeleteModal
        open={confirmClear}
        title="Clear the auto-generated PPM schedule?"
        message={clearAutoPpmMessage(clearPreview, clearIncludeAssigned)}
        confirmLabel="Clear visits"
        variant="destructive"
        busy={busy}
        error={clearError}
        onConfirm={clearAutoPpm}
        onCancel={() => setConfirmClear(false)}
      >
        {clearPreview && clearPreview.assignedNotAccepted > 0 ? (
          <label className="mt-3 flex items-start gap-2 text-sm text-ele-body">
            <input
              type="checkbox"
              checked={clearIncludeAssigned}
              onChange={(e) => setClearIncludeAssigned(e.target.checked)}
              className="mt-1"
            />
            <span>
              Also remove the {clearPreview.assignedNotAccepted} with an engineer on them who has not accepted yet. Leave this
              unticked to keep them.
            </span>
          </label>
        ) : null}
      </ConfirmDeleteModal>

      <NotifyModal target={notifyTarget} sending={notifySending} onSend={confirmNotify} onSkip={() => setNotifyTarget(null)} />

      <DispatchConfirmModal
        target={dispatchConfirm}
        sending={dispatchSending}
        onConfirm={() => void confirmDispatch(true)}
        onSkip={() => void confirmDispatch(false)}
      />

      {selectedEntry && (
        <EntryDetailModal
          entry={selectedEntry}
          engineerName={engineerName}
          engineers={data?.engineers ?? []}
          busy={deleteBusy}
          error={deleteError}
          assigning={assignBusy}
          assignError={assignError}
          rescheduling={rescheduleBusy}
          rescheduleError={rescheduleError}
          onReschedule={(jobId, day) => void onReschedulePopover(jobId, day, selectedEntry?.workDay ?? null)}
          onDelete={deleteSelected}
          onAssign={onAssignEngineer}
          onChangeCrew={(jobId, engineerId, action) => void changeCrew(jobId, engineerId, action)}
          onDaysChanged={() => {
            setSelectedEntry(null);
            setNotice({ tone: "success", text: "The job's days were changed. It is the same job, with the same reference and invoice." });
            void load();
          }}
          onAssignCallout={(calloutId, engineerId) => void assignCalloutEngineer(calloutId, engineerId)}
          onAcceptProposal={(jobId, engineerId, scheduledFor) => void acceptProposedJob(jobId, engineerId, scheduledFor)}
          // Session ARA (S-7), D57: dispatching an engineer to a call-out is admin AND office since
          // the Director's 23 September 2026 ruling (PATCH /api/callouts/:id/assign, callout.routes.ts).
          canDispatchCallout={user?.role === "admin" || user?.role === "office"}
          onRecordBackdated={() => {
            const entry = selectedEntry;
            setSelectedEntry(null);
            setDeleteError(null);
            setBackdatedEntry(entry);
          }}
          pauseResumeBusy={pauseResumeBusy}
          pauseResumeError={pauseResumeError}
          onPause={(jobId, holdReason, reasonText) => void pauseJob(jobId, holdReason, reasonText)}
          onResume={(jobId) => void resumeJob(jobId)}
          onCancelJob={() => {
            const entry = selectedEntry;
            setSelectedEntry(null);
            setDeleteError(null);
            setCancellingEntry(entry);
          }}
          onClose={() => {
            // Session ALS: a write in flight now holds the popover open, not just a delete. It
            // guarded deleteBusy alone, so Escape, a backdrop click or the close control could all
            // dismiss the panel mid-assign -- and assignEngineer's catch writes into assignError,
            // which this very handler had just cleared. The failure was then reported NOWHERE at the
            // time and surfaced on whichever entry was opened next, attributed to that one. Closing
            // is simply refused while a write is running, exactly as it already was for a delete.
            // Session AQY: pauseResumeBusy joins the same guard, for the same reason.
            if (deleteBusy || assignBusy || rescheduleBusy || pauseResumeBusy) return;
            setSelectedEntry(null);
            setDeleteError(null);
            setAssignError(null);
            setRescheduleError(null);
            setPauseResumeError(null);
          }}
        />
      )}

      {/* Session AMF: the one control for the three undated job types. It reloads the calendar on
          success, so whatever was raised appears on the "Needs scheduling" panel immediately
          rather than after a manual refresh. */}
      {raise && (
        <RaiseWorkModal
          preset={raise.type ? { type: raise.type } : undefined}
          onClose={() => setRaise(null)}
          onRaisedSafed={() => {
            setRaise(null);
            setNotice({ tone: "success", text: "SAFed test job raised. It is on the calendar if you chose a day, otherwise on the Needs scheduling panel." });
            void load();
          }}
          onRaised={(result) => {
            setRaise(null);
            setNotice({ tone: "success", text: raisedWorkMessage(result) });
            void load();
          }}
        />
      )}

      {/* Record a scheduled visit as completed on its real past date (Session AEB): the same
          modal the lift record offers, reached from the calendar entry's popover. */}
      {backdatedEntry && (
        <BackdatedCompletionModal
          jobId={backdatedEntry.id}
          liftLabel={`${backdatedEntry.ref ?? "Lift"}${backdatedEntry.siteName ? ` | ${backdatedEntry.siteName}` : ""}`}
          scheduledForIso={backdatedEntry.date}
          onClose={() => setBackdatedEntry(null)}
          onRecorded={() => void load()}
        />
      )}

      {/* Cancel-with-attended-time (Session AQY, D-AQX-4): works for any job type, reached from the
          calendar entry's popover. */}
      {cancellingEntry && (
        <CancelJobModal
          liftLabel={`${cancellingEntry.ref ?? cancellingEntry.reference ?? "Lift"}${cancellingEntry.siteName ? ` | ${cancellingEntry.siteName}` : ""}`}
          onClose={() => setCancellingEntry(null)}
          onConfirm={async (body) => {
            const res = await api.schedule.cancelJob(cancellingEntry.id, body);
            await load();
            return { travelLogged: res.travelLogged, travelSkippedReason: res.travelSkippedReason, travelNotRechecked: res.travelNotRechecked ?? [] };
          }}
        />
      )}

      {/* Session ARA (S-14): opens to whichever day is currently in view. */}
      {showDayRoute && (
        <DayRouteModal initialDate={startOfUtcDay(cursor)} engineers={data?.engineers ?? []} onClose={() => setShowDayRoute(false)} />
      )}

      {/* Job type legend: icon + colour + label per type, and the same control as the Type filter below
          (tap a type to show only that type; tap it again, or Show all, to reset). */}
      <div role="group" aria-label="Job types" data-testid="type-legend" className="mb-3 flex flex-wrap items-center gap-1.5">
        {LEGEND.map((l) => {
          const st = jobTypeStyle(l.icon);
          const Icon = jobTypeIcon(l.icon);
          const on = typeFilter === l.key;
          return (
            <button
              key={l.key}
              type="button"
              aria-pressed={on}
              onClick={() => setTypeFilter(on ? "all" : l.key)}
              className={`inline-flex min-h-8 max-sm:min-h-11 items-center gap-1.5 rounded-full border px-2.5 font-menu text-xs font-semibold uppercase tracking-wide transition-colors ${
                on ? `${st.bg} ${st.onBg} ${st.border}` : `${st.border} bg-ele-inset text-ele-strong hover:bg-ele-row-hover`
              }`}
            >
              <Icon size={13} aria-hidden="true" className={on ? "" : st.text} />
              {l.label}
            </button>
          );
        })}
        {typeFilter !== "all" && (
          <button type="button" onClick={() => setTypeFilter("all")} className="min-h-8 max-sm:min-h-11 rounded-full px-2.5 font-menu text-xs uppercase tracking-wide text-ele-blue underline">
            Show all
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="ele-touch-row mb-4 flex flex-wrap items-center gap-3">
        <label className="flex min-w-0 max-w-full items-center gap-2 text-xs text-ele-body">
          <span className="font-menu tracking-wider">Type</span>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
            className="min-w-0 max-w-full rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-sm text-ele-strong outline-none focus:border-ele-green"
          >
            <option value="all">All</option>
            <option value="service">Service</option>
            <option value="callout">Call-out</option>
            <option value="repair">Repair / tech visit</option>
            <option value="inspection">Inspection</option>
            <option value="safed">SAFed test</option>
            <option value="project">Project</option>
            <option value="pending">Closed, awaiting release</option>
          </select>
        </label>
        <label className="flex min-w-0 max-w-full items-center gap-2 text-xs text-ele-body">
          <span className="font-menu tracking-wider">Engineer</span>
          <select
            value={engineerFilter}
            onChange={(e) => setEngineerFilter(e.target.value)}
            className="min-w-0 max-w-full rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-sm text-ele-strong outline-none focus:border-ele-green"
          >
            <option value="all">All</option>
            <option value="unassigned">Unassigned</option>
            {(data?.engineers ?? []).map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {notice && (
        <p
          role={notice.tone === "error" ? "alert" : "status"}
          className={`mb-4 rounded-ele border px-3 py-2 text-sm ${
            notice.tone === "error"
              ? "border-ele-danger bg-ele-surface text-ele-strong"
              : notice.tone === "warning"
                ? "border-amber-500 bg-amber-500/10 text-amber-300"
                : "border-ele-green bg-ele-surface text-ele-strong"
          }`}
        >
          {notice.text}
        </p>
      )}

      {/* Session SC9A: every job on hold, in ONE capped strip (derived live from the job status; hidden when none). Not a new page. */}
      <HeldJobsStrip reloadKey={heldKey} />

      {/* Session ARA (S-9): who is on standby for the week(s) currently in view, resolved live from
          the rota + any office override. Skipped on the year view, which shows month counts only and
          fetches a whole year's worth of weeks -- a banner would either be 52 lines long or silently
          only show the first, neither of which is honest. */}
      {!loading && !error && data && view !== "year" && (
        <StandbyBannerPanel weeks={data.standbyWeeks ?? []} />
      )}

      {/* Session ARA (S-15): an undated SERVICE (PPM) visit is never ordinary backlog -- it is a
          signal something went wrong upstream (see UNDATED_SERVICE_WHERE's own header comment,
          schedulingApi.routes.ts). Rendered ABOVE the ordinary Needs-scheduling panel, unconditional
          on the type/engineer filters (an alarm should not disappear because a view filter hid it --
          it is reporting a data problem, not offering a work list to plan from). */}
      {!loading && !error && data && (data.unscheduled?.servicesNeedingAttention?.length ?? 0) > 0 && (
        <ServiceNeedsAttentionPanel
          jobs={data.unscheduled?.servicesNeedingAttention ?? []}
          engineerName={engineerName}
          drag={drag}
          capped={data.unscheduled?.capped === true}
          shownLimit={data.unscheduled?.shownLimit}
        />
      )}

      {/* Session ALS: work that belongs to no date, so it can never appear in a day cell and until
          now appeared nowhere at all. Rendered ABOVE the calendar and on EVERY view, because it is
          unactioned work rather than a plan, and because a desktop user on the week grid needs it as
          much as a phone user on the day view. Renders nothing when there is nothing waiting. */}
      {!loading && !error && data && needsScheduling.length > 0 && (
        <NeedsSchedulingPanel
          entries={needsScheduling}
          engineerName={engineerName}
          drag={drag}
          capped={data.unscheduled?.capped === true}
          shownLimit={data.unscheduled?.shownLimit}
        />
      )}

      {loading ? (
        <Loader />
      ) : error || !data ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the schedule. Please try again in a moment.
        </p>
      ) : view === "year" ? (
        <YearView year={cursor.getUTCFullYear()} monthCounts={yearMonthCounts} onOpenMonth={openMonth} />
      ) : visibleCount === 0 && view !== "day" && view !== "board" ? (
        <p className="rounded-ele border border-ele-purple-accent bg-ele-surface p-8 text-center text-sm text-ele-body">
          No jobs scheduled for this period. PPM visits are generated automatically from active contracts.
        </p>
      ) : view === "board" ? (
        // Session ARA (S-1). Rendered even for an EMPTY week, like the day view and unlike the week
        // grid: its cells are the drop targets for the undated work in Needs scheduling, and a week
        // with nothing in it yet is exactly when the office is filling it.
        <EngineerWeekBoardView
          days={weekDays(cursor)}
          entriesByDay={entriesByDay}
          engineerName={engineerName}
          engineers={data?.engineers ?? []}
          todayKey={todayKey}
          drag={drag}
        />
      ) : view === "day" ? (
        <DayView
          day={startOfUtcDay(cursor)}
          entriesByDay={entriesByDay}
          trainingByDay={trainingByDay}
          engineerName={engineerName}
          engineers={data?.engineers ?? []}
          todayKey={todayKey}
          drag={drag}
          onBulkAssign={bulkAssignEngineer}
          bulkAssignBusy={bulkAssignBusy}
        />
      ) : view === "week" ? (
        <WeekView days={weekDays(cursor)} entriesByDay={entriesByDay} trainingByDay={trainingByDay} projectSpans={projSpans} engineerName={engineerName} todayKey={todayKey} drag={drag} />
      ) : (
        <MonthView cursorMonth={cursor.getUTCMonth()} days={monthGridDays(cursor)} entriesByDay={entriesByDay} trainingByDay={trainingByDay} projectSpans={projSpans} engineerName={engineerName} todayKey={todayKey} drag={drag} />
      )}
    </div>
  );
}

// "Notify customer?" after a service-visit reschedule (Session AG Item 3). The reschedule is already
// saved; this only decides whether to send the confirmation email. Escape / backdrop click defaults
// to "No, skip" (never sends). When the customer has no email on file, sending is not offered.
function NotifyModal({ target, sending, onSend, onSkip }: { target: NotifyTarget | null; sending: boolean; onSend: () => void; onSkip: () => void }) {
  if (!target) return null;
  const who = target.customerName?.trim() || target.siteName || "the customer";
  const dateLabel = formatDateLong(startOfUtcDay(target.date));
  const hasEmail = Boolean(target.customerEmail?.trim());
  return (
    <ModalShell ariaLabel="Notify customer" onClose={() => !sending && onSkip()} closeOnBackdrop width="max-w-md" panelClassName="p-6 text-left">
        <h2 className="mb-2 font-heading text-xl text-ele-strong">Notify customer?</h2>
        <p className="text-sm text-ele-strong">
          {who}
          {target.customerName && target.siteName ? <span className="text-ele-body"> at {target.siteName}</span> : null}
        </p>
        <p className="mt-1 text-sm text-ele-body">New visit date: {dateLabel}.</p>
        {hasEmail ? (
          <p className="mt-3 text-sm text-ele-body">Do you want to send a confirmation email to {target.customerEmail}?</p>
        ) : (
          <p className="mt-3 text-sm text-ele-body">This customer has no email on file, so a confirmation email cannot be sent.</p>
        )}
        <div className={`ele-touch-row mt-5 gap-2 ${MODAL_ACTIONS}`}>
          <button
            type="button"
            onClick={onSkip}
            disabled={sending}
            className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50 ${MODAL_ACTION_BTN}`}
          >
            {hasEmail ? "No, skip" : "Close"}
          </button>
          {hasEmail && (
            <Button onClick={onSend} disabled={sending} className={MODAL_ACTION_BTN}>
              {sending ? "Sending..." : "Send notification"}
            </Button>
          )}
        </div>
    </ModalShell>
  );
}

// "Assign engineer?" branded confirm (Session AKA, closing the immediacy gap AJI's own handoff
// flagged). Shown BEFORE the assignment is written -- confirming or skipping calls the SAME
// reassign endpoint once, so there is never a real send with a stray Gmail draft left behind it.
// Escape / backdrop click defaults to "Just assign" (never sends), matching NotifyModal. When
// there is no email or phone on file, the notify option is not offered.
function DispatchConfirmModal({
  target,
  sending,
  onConfirm,
  onSkip,
}: {
  target: DispatchConfirmTarget | null;
  sending: boolean;
  onConfirm: () => void;
  onSkip: () => void;
}) {
  if (!target) return null;
  const who = target.customerName?.trim() || target.siteName || "the customer";
  const hasEmail = Boolean(target.customerEmail?.trim());
  const hasPhone = Boolean(target.customerPhone?.trim());
  const channels = [hasEmail ? `an email to ${target.customerEmail}` : null, hasPhone ? `a text to ${target.customerPhone}` : null].filter(
    (c): c is string => c != null,
  );
  return (
    <ModalShell ariaLabel="Assign engineer" onClose={() => !sending && onSkip()} closeOnBackdrop width="max-w-md" panelClassName="p-6 text-left">
      <h2 className="mb-2 font-heading text-xl text-ele-strong">Assign {target.engineerLabel}?</h2>
      <p className="text-sm text-ele-strong">
        {who}
        {target.customerName && target.siteName ? <span className="text-ele-body"> at {target.siteName}</span> : null}
      </p>
      <AccountOnStopLine customerId={target.customerId} />
      {channels.length > 0 ? (
        <p className="mt-3 text-sm text-ele-body">
          Send {channels.join(" and ")} now to say the engineer is on the way?
        </p>
      ) : (
        <p className="mt-3 text-sm text-ele-body">This customer has no email or phone on file, so there is nothing to notify.</p>
      )}
      <div className={`ele-touch-row mt-5 gap-2 ${MODAL_ACTIONS}`}>
        <button
          type="button"
          onClick={onSkip}
          disabled={sending}
          className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50 ${MODAL_ACTION_BTN}`}
        >
          Just assign
        </button>
        {channels.length > 0 && (
          <Button onClick={onConfirm} disabled={sending} className={MODAL_ACTION_BTN}>
            {sending ? "Assigning..." : "Assign and notify now"}
          </Button>
        )}
      </div>
    </ModalShell>
  );
}

function EntryCard({
  entry,
  engineerName,
  drag,
  dragHint = "drag to reschedule",
  subtitle = null,
  compact = false,
  todayKey,
}: {
  entry: CalEntry;
  engineerName: Map<string, string>;
  drag: DragApi;
  // Week/month grids drag onto a day cell to reschedule; the day view drags onto an engineer lane
  // to assign instead. The tooltip should say which.
  dragHint?: string;
  // Session ALS: one extra line, used only by the needs-scheduling panel ("Urgent", "waiting 3
  // days"). A day-cell card passes nothing and renders exactly as it always has.
  subtitle?: string | null;
  // Session ARA (S-1): on the engineer-by-week board the ROW already names the engineer, so the
  // card's own assignee line would repeat it in every cell of a small grid. Hidden there, and
  // nowhere else: the default keeps every other caller's card byte-identical.
  compact?: boolean;
  // Session ARA (S-10): the calendar's own "today" (keyOf(startOfUtcDay(new Date())), recomputed on
  // every SchedulingPage render), used only to decide the Overdue badge below. Optional so a caller
  // with no meaningful "today" (there is none today) can simply omit it; isEntryLate then never fires.
  todayKey?: string;
}) {
  const style = entryStyle(entry);
  const done = entry.kind === "job" && entry.status === "complete";
  const assignee = entry.kind === "project" ? null : entry.assignedTo ? engineerName.get(entry.assignedTo) ?? "Engineer" : "Unassigned";
  const canDrag = entry.kind === "job" && REASSIGNABLE.has(entry.status);
  // Session ALE: a touchscreen cannot fire HTML5 drag, so on a coarse pointer the card stops
  // ADVERTISING one -- no grab cursor, no drag hint in the title. It stays draggable (see the rule
  // on DragApi.coarsePointer: a trackpad-equipped tablet reports coarse and can still drag), and it
  // stays fully usable either way, because tapping it opens the entry popover, which is where a
  // touch user assigns the engineer and (since this session) changes the date.
  const offerDrag = canDrag && !drag.coarsePointer;
  const moving = drag.movingId === entry.id;
  // Session ARA (S-2): the scheduled time and the duration estimate, jobs only -- a project's `date`
  // is a placement day, not a booked time, and neither carries an estimate of its own.
  const timeLabel = entry.kind === "job" && entry.date ? formatTime(entry.date) : null;
  const durationLabel = entry.kind === "job" && entry.durationMins != null && entry.durationMins > 0 ? fmtDuration(entry.durationMins) : null;
  // Session ARA (S-10). Unassigned is suppressed when `compact`: on the engineer-by-week board a
  // compact card sits in a LANE already labelled by engineer (including the "Unassigned" lane
  // itself), so a second "Unassigned" pill on every card in that one row would be pure repetition of
  // what the row heading already says -- the same reasoning that already hides the plain-text
  // assignee line below on a compact card. Overdue carries no such reasoning (a day column header is
  // easy to miss at a glance), so it is NOT gated on compact.
  const unassigned = isEntryUnassigned(entry) && !compact;
  const late = todayKey ? isEntryLate(entry, todayKey) : false;
  // Session SC9A: the card's ONE state pill, the most urgent (entrapment, overdue, on hold, SLA held, follow-on works), else none.
  const cardState = entry.kind === "project" ? null : primaryStatePill({
    status: entry.kind === "job" ? entry.status : undefined,
    entrapment: entry.entrapment,
    overdue: late,
    holdReason: entry.holdReason,
    holdReasonText: entry.holdReasonText,
    slaHeldLabel: entry.slaHeldLabel,
    followOnWorks: entry.followOnWorks,
  });
  return (
    <div
      data-testid="cal-card"
      draggable={canDrag}
      onDragStart={
        canDrag
          ? (e) => {
              e.dataTransfer.setData("text/plain", entry.id);
              e.dataTransfer.effectAllowed = "move";
              drag.onCardDragStart(entry.id, entry.workDay ?? null);
            }
          : undefined
      }
      onDragEnd={canDrag ? drag.onCardDragEnd : undefined}
      onClick={() => drag.onSelect(entry)}
      // Session ALE: a real control, not a click-only div. These cards were <div onClick> with no
      // tabIndex, role or key handler anywhere in the file, so a keyboard or screen-reader user
      // could not open a visit at all -- the WCAG 2.1.1 failure the July AEE audit already
      // recorded for this page. Enter/Space now open the popover exactly as a tap does.
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          drag.onSelect(entry);
        }
      }}
      aria-haspopup="dialog"
      title={offerDrag ? `Open this visit; ${dragHint}` : "Open this visit"}
      style={entry.kind === "job" && !done ? { backgroundImage: `linear-gradient(${jobTypeTint(entry.type)}, ${jobTypeTint(entry.type)})` } : undefined}
      className={`min-w-0 rounded-ele border bg-ele-inset p-1.5 text-xs ${done ? "border-ele-purple-accent opacity-60" : style.border} ${
        offerDrag ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
      } ${moving ? "opacity-50" : ""}`}
    >
      {/* Job type clarity: the type badge (icon + label) first, then the site, then when; the status
          chips sit in ONE row with no repeats, and the long reference goes last, small and muted. */}
      <div className="flex items-center justify-between gap-1">
        {entry.kind === "project" ? (
          <span className="font-menu max-md:text-xs text-[10px] font-semibold uppercase tracking-wide text-ele-loler">Project</span>
        ) : (
          <JobTypeBadge type={entry.type} equipmentAsset={entry.equipmentAsset} size="sm" />
        )}
        {entry.autoGenerated && (
          <span
            title="Auto-scheduled from the PPM programme"
            aria-label="Auto-scheduled from the PPM programme"
            className="flex-none rounded-sm border border-ele-green px-1 font-menu text-[8px] font-bold uppercase tracking-wide text-ele-green"
          >
            Auto
          </span>
        )}
      </div>
      <div className="mt-1 break-words font-heading text-sm leading-tight text-ele-strong">{entry.siteName || "Site TBC"}</div>
      {/* Session BDM: one day of a multi-day job: the same job on each of its days. */}
      {entry.dayLabel && (
        <div data-testid="card-day-label" className="font-menu max-md:text-xs text-[10px] font-semibold text-ele-strong">
          {entry.dayLabel}
        </div>
      )}
      {/* Session ARA (S-2): the scheduled time and, when the platform holds one, the dispatch-board
          duration estimate, e.g. "09:00 · 45 min". */}
      {(timeLabel || durationLabel) && (
        <div className="font-menu max-md:text-xs text-[10px] text-ele-body opacity-80">{[timeLabel, durationLabel].filter(Boolean).join(" · ")}</div>
      )}
      <div className="mt-1 flex flex-wrap items-center gap-1">
        {/* Session SC9A: ONE state pill (statusRegistry.ts, the fixed urgency order). A state the type colour is overridden for
            and that has no pill of its own (pending approval) is still named in its own words. */}
        {cardState ? (
          <StatusPill state={cardState} title={cardState.kind === "overdue" ? "This visit's date has passed and it is still open" : undefined} />
        ) : (
          entry.kind === "job" &&
          style.label !== jobTypeStyleFor(entry.type, { equipmentAsset: entry.equipmentAsset }).label && (
            <span className={`font-menu max-md:text-xs text-[10px] tracking-wide ${style.text}`}>{style.label}</span>
          )
        )}
        {acceptanceState(entry) === "pending" && (
          <StatusPill word="Not accepted" tone="amber" title="Assigned, but the engineer has not accepted it yet" />
        )}
        {unassigned && <StatusPill word="Unassigned" tone="grey" title="No engineer is assigned to this visit yet" />}
        {entry.isNonContract && <StatusPill word="Non-contract" tone="amber" />}
      </div>
      {subtitle && <div className="mt-0.5 max-md:text-xs text-[10px] text-amber-400">{subtitle}</div>}
      {assignee && !compact && !(assignee === "Unassigned" && unassigned) && (
        <div className={`mt-0.5 max-md:text-xs text-[10px] ${assignee === "Unassigned" ? "text-ele-body opacity-70" : "text-ele-blue"}`}>{assignee}</div>
      )}
      {/* Session AOH: the customer's name is reached for before the "Lift" literal (a lift-less
          planned repair has no ELE number). Truncated to one line, the full value in the tooltip. */}
      <div
        data-testid="card-ref"
        className="mt-1 truncate font-menu max-md:text-xs text-[10px] text-ele-body opacity-60"
        title={entry.reference ?? entry.ref ?? undefined}
      >
        {entry.reference ?? entry.ref ?? entry.customerName ?? (entry.kind === "project" ? "Project" : "Lift")}
      </div>
    </div>
  );
}


// The clicked-entry detail popover (Session AR Item 9): shows the visit's details and, for an
// unstarted scheduled job, a two-step Delete. A project or an already-actioned job shows a note
// that it cannot be deleted from the calendar.
// Session ASM (Phase 6, Director decision D83): the engineers booked on a visit BESIDES its primary.
// Up to three (four on the job in all). Each is named, or an unnamed booked slot (a job raised two-man
// before anybody was chosen), which naming the next engineer fills. The primary stays the one engineer
// who accepts the job and keeps its sheet; the crew see it on their phones and read its RAMS, and their
// hours reach their own timesheets when the visit is approved.
const MAX_ADDITIONAL_CREW = 3;
function CrewRows({
  entry,
  engineers,
  engineerName,
  editable,
  busy,
  onChange,
}: {
  entry: CalEntry;
  engineers: { id: string; name: string }[];
  engineerName: Map<string, string>;
  editable: boolean;
  busy: boolean;
  onChange: (engineerId: string, action: "add" | "remove") => void;
}) {
  const crew = entry.crew ?? [];
  const named = crew.filter((m): m is { engineerId: string; name: string | null } => !!m.engineerId);
  const unnamed = crew.length - named.length;
  const full = crew.length >= MAX_ADDITIONAL_CREW && unnamed === 0;
  const candidates = engineers.filter((eng) => eng.id !== entry.assignedTo && !named.some((m) => m.engineerId === eng.id));
  if (!editable && crew.length === 0) return null;
  return (
    <div className="py-0.5">
      <dt className="text-ele-body">Also attending</dt>
      <dd className="mt-1 space-y-1">
        {named.map((m) => (
          <div key={m.engineerId} className="flex items-center justify-between gap-2">
            <span className="text-ele-strong">{m.name ?? engineerName.get(m.engineerId) ?? "Engineer"}</span>
            {editable && (
              <ConfirmButton
                onConfirm={() => onChange(m.engineerId, "remove")}
                label="Remove"
                busy={busy}
                busyLabel="Removing..."
                title="Takes this engineer off the visit. The visit's own engineer is unchanged."
                className="min-h-11 rounded-ele border border-ele-purple-accent px-3 font-heading text-xs tracking-wide text-ele-body transition hover:bg-ele-inset disabled:opacity-50"
              />
            )}
          </div>
        ))}
        {unnamed > 0 && (
          <p className="text-xs text-ele-body">
            {unnamed === 1 ? "One more engineer is booked, not yet named." : `${unnamed} more engineers are booked, not yet named.`}
          </p>
        )}
        {editable && !entry.assignedTo && (
          <p className="text-xs text-ele-body opacity-80">Assign the visit's own engineer first, then add the others attending.</p>
        )}
        {editable && entry.assignedTo && !full && (
          <select
            aria-label="Add to the crew"
            value=""
            disabled={busy || candidates.length === 0}
            onChange={(e) => e.target.value && onChange(e.target.value, "add")}
            className="min-h-11 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50"
          >
            <option value="">{candidates.length === 0 ? "Nobody else to add" : "Add someone else attending..."}</option>
            {candidates.map((eng) => (
              <option key={eng.id} value={eng.id}>
                {eng.name}
              </option>
            ))}
          </select>
        )}
        {editable && full && <p className="text-xs text-ele-body opacity-80">Four engineers is the most one visit can take.</p>}
      </dd>
    </div>
  );
}

function EntryDetailModal({
  entry,
  engineerName,
  engineers,
  busy,
  error,
  assigning,
  assignError,
  rescheduling,
  rescheduleError,
  onReschedule,
  onDelete,
  onAssign,
  onChangeCrew,
  onDaysChanged,
  onAssignCallout,
  onAcceptProposal,
  canDispatchCallout,
  onRecordBackdated,
  pauseResumeBusy,
  pauseResumeError,
  onPause,
  onResume,
  onCancelJob,
  onClose,
}: {
  entry: CalEntry;
  engineerName: Map<string, string>;
  engineers: { id: string; name: string }[];
  busy: boolean;
  error: string | null;
  assigning: boolean;
  assignError: string | null;
  // Session ALE: rescheduling from here is the ONLY way to move a visit on a touchscreen -- the
  // week/month day-cell drop is HTML5 drag, which a touchscreen cannot fire. Its own busy/error
  // pair mirrors the engineer picker beside it.
  rescheduling: boolean;
  rescheduleError: string | null;
  onReschedule: (jobId: string, day: Date) => void;
  onDelete: () => void;
  // Assign or unassign an engineer on this job. Only offered on a job entry (not a project).
  onAssign: (jobId: string, engineerId: string | null) => void;
  // Session ASM (Phase 6, D83): add a named engineer to the crew, or take one off.
  onChangeCrew?: (jobId: string, engineerId: string, action: "add" | "remove") => void;
  // Session BDM: the job's days were changed from the visit details (set, extend, shorten, move, who is working).
  onDaysChanged: () => void;
  // Session ALS: give an OPEN call-out its first engineer. A different route (it CREATES the
  // dispatch job) and a different permission (admin only), so it is a separate callback rather than
  // a branch inside onAssign -- the two writes must never be confused for one another.
  onAssignCallout: (calloutId: string, engineerId: string) => void;
  // Session SC5: accept the platform's proposed engineer (and day, when the job has none) for an unassigned repair or technician visit.
  onAcceptProposal?: (jobId: string, engineerId: string, scheduledFor: string | null) => void;
  // Whether this signed-in user may dispatch a call-out at all (admin). False for office, which is
  // told so plainly instead of being shown a control the server would refuse.
  canDispatchCallout: boolean;
  // Record this visit as already done on a real past date (Session AEB). Offered on a
  // scheduled/in-progress SERVICE visit only; the host swaps this popover for the entry modal.
  onRecordBackdated?: () => void;
  // Session AQY (D-AQX-1): pause a job (awaiting parts) / resume a paused one. Works for any job
  // type -- unlike onRecordBackdated above, which is service-only.
  pauseResumeBusy: boolean;
  pauseResumeError: string | null;
  onPause: (jobId: string, holdReason: JobHoldReasonKey, reasonText: string) => void;
  onResume: (jobId: string) => void;
  // Session AQY (D-AQX-4): opens the (separate, host-owned) CancelJobModal for this entry. Only
  // ever called for entry.kind === "job" -- see cancellable below.
  onCancelJob: () => void;
  onClose: () => void;
}) {
  const style = entryStyle(entry);
  const deletable = entry.kind === "job" && entry.status === "scheduled";
  const backdatable = entry.kind === "job" && entry.type === "service" && (entry.status === "scheduled" || entry.status === "in_progress");
  // Session AQY: pausable from the same states a job is REASSIGNABLE from before parts_pending joins
  // that set (scheduled or in_progress); a paused job offers Resume instead.
  const pausable = entry.kind === "job" && (entry.status === "scheduled" || entry.status === "in_progress");
  const paused = entry.kind === "job" && entry.status === "parts_pending";
  // Session AQY (D-AQX-4): cancellable from any still-open job state, INCLUDING paused -- the same
  // REASSIGNABLE set the server itself cancels from. Never offered for an open call-out with no Job
  // yet (kind === "callout"): that shape is cancelled from the Call-outs page instead, which already
  // says so in the note below.
  const cancellable = entry.kind === "job" && REASSIGNABLE.has(entry.status);
  const [pausing, setPausing] = useState(false);
  const [pauseReason, setPauseReason] = useState("");
  // Session RC1: the overtime agreement as last saved from here (the calendar reloads it on the next read).
  const [techOvertime, setTechOvertime] = useState({ agreedAt: entry.techOvertimeAgreedAt ?? null, agreedByName: entry.techOvertimeAgreedByName ?? null });
  // Session SC9A: the reason is picked from one fixed list; Other needs a few words.
  const [pauseHoldReason, setPauseHoldReason] = useState<JobHoldReasonKey>("awaiting_parts");
  // Reassignable mirrors the same set the drag-to-reschedule flow uses (REASSIGNABLE): a job that
  // is still open. A completed or pending-approval visit's engineer is a historical fact, not editable here.
  const assignable = entry.kind === "job" && REASSIGNABLE.has(entry.status);
  // An OPEN call-out (Session ALS): no Job row yet, so it takes the call-out assign route, which is
  // admin only. An office user is told that in words rather than shown a dropdown whose every use
  // would be refused by the server.
  const isOpenCallout = entry.kind === "callout";
  // WHY the engineer cannot be changed, in the office's own words. Session ALS: a completed or
  // pending-approval card is tappable and always was, and the popover simply showed a read-only
  // Engineer row with no explanation -- so tapping a visit and finding no dropdown read as the
  // feature being broken rather than as a deliberate rule. Every non-service card on the live
  // calendar at the time of the Director's report was in exactly one of those two states.
  const assignBlockedReason =
    entry.kind !== "job" || assignable
      ? null
      : entry.status === "pending_approval"
        ? "The engineer has submitted this visit for approval, so who attended is now part of the record. Return it from the service record if that needs to change."
        : entry.status === "complete"
          ? "This visit is complete, so who attended is part of the record and is no longer editable here."
          : "This visit is no longer open, so its engineer can no longer be changed here.";
  // Session ALS: an engineer who has since been deactivated is not in `engineers`, so the select
  // matched no option -- and a <select> with no matching value falls back to its FIRST option, which
  // is "Unassigned". It therefore positively reported "Unassigned" for a visit that was assigned and
  // still on that person's phone; worse, because that option was already selected, choosing it fired
  // no change event, so the one visit that most needed reassigning could not even be unassigned.
  // Adding the real value as an option makes the control tell the truth and restores both writes.
  const orphanEngineerId =
    entry.kind === "job" && entry.assignedTo && !engineers.some((eng) => eng.id === entry.assignedTo) ? entry.assignedTo : null;
  // Session ADF: a job whose visit is done (completed) or submitted for approval has a service
  // record, so the popup routes straight to that visit's ServiceRecordPanel. A scheduled or
  // in-progress visit has no sheet yet, so instead of offering nothing it now SAYS SO
  // (recordPending, Session ALU) beside the delete note. Both read the one shared rule above,
  // which the day-view row also reads.
  const recordHref = serviceRecordHrefFor(entry);
  const recordPending = recordPendingNote(entry);
  const assignee = entry.kind === "project" ? null : entry.assignedTo ? engineerName.get(entry.assignedTo) ?? "Engineer" : "Unassigned";
  const dateLabel = entry.date
    ? formatDateLong(startOfUtcDay(new Date(entry.date)))
    : "Not scheduled";
  return (
    <ModalShell ariaLabel="Visit details" onClose={() => !busy && onClose()} closeOnBackdrop width="max-w-md" panelClassName="p-6">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            {/* Session ATD (S-8 follow-up): a real deep link to the lift record when this entry has
                one, the same styling other record pages use for a cross-link (SiteDetailPage's
                customer link). A project or an undated repair/call-out with no lift resolved keeps
                the plain heading it always had. */}
            <h2 className="font-heading text-xl text-ele-strong">
              {entry.kind === "job" && entry.assetId ? (
                <Link to={`/office/lifts/${encodeURIComponent(entry.assetId)}`} className="hover:text-ele-green hover:underline">
                  {entry.reference ?? entry.ref ?? "Lift"}
                </Link>
              ) : (
                entry.reference ?? entry.ref ?? (entry.kind === "project" ? "Project" : "Lift")
              )}
            </h2>
            <span className={`font-menu text-xs tracking-wide ${style.text}`}>{style.label}</span>
          </div>
          {/* Session ALE: a real 44px target. This was a bare glyph of roughly 14 x 20px, below
              the touch floor at every width, and it is the popover control a thumb reaches for. */}
          <button
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 -mt-2 flex min-h-11 min-w-11 items-center justify-center rounded-ele text-ele-body hover:text-ele-green"
          >
            ✕
          </button>
        </div>
        <dl className="space-y-1.5 text-sm">
          {/* Session ATD (S-8 follow-up): real deep links to the site and customer records, when this
              entry carries their ids -- the same cross-link pattern the other record pages use. A
              job with no site resolved (an undated lift-less repair before it is scheduled) or no
              customer (an unmatched emergency caller) simply keeps the plain text it always had. */}
          <DetailRow
            label="Site"
            value={
              entry.kind === "job" && entry.siteId ? (
                <Link to={`/office/sites/${encodeURIComponent(entry.siteId)}`} className="text-ele-green underline-offset-2 hover:underline">
                  {entry.siteName || "Site TBC"}
                </Link>
              ) : (
                entry.siteName || "Site TBC"
              )
            }
          />
          {entry.kind === "job" && entry.customerId && (
            <DetailRow
              label="Customer"
              value={
                <>
                  <Link to={`/office/customers/${encodeURIComponent(entry.customerId)}`} className="text-ele-green underline-offset-2 hover:underline">
                    {entry.customerName ?? "Customer"}
                  </Link>
                  <AccountSymbol customerId={entry.customerId} />
                </>
              }
            />
          )}
          {/* Session ALE: the same "still open" rule (REASSIGNABLE, via assignable) governs both
              the date and the engineer -- a completed or pending-approval visit is a historical
              fact and neither is editable. Dates are UTC throughout this page, so the value and
              the parse both use the UTC parts and never the local ones. */}
          {assignable ? (
            <div className="flex items-center justify-between gap-2 py-0.5">
              <dt className="text-ele-body">Date</dt>
              <dd>
                {/* Session ALS dropped the `&& entry.date` half of this gate. An undated REPAIR now
                    reaches this popover (it never could before), and it is precisely the entry that
                    needs a date typed into it -- the old gate would have shown the one job with no
                    date a read-only "Not scheduled" and no way to fix it. An empty value is a valid
                    empty date input; picking one calls the same reschedule the drag already uses. */}
                <input
                  type="date"
                  aria-label="Visit date"
                  value={entry.date ? entry.date.slice(0, 10) : ""}
                  disabled={rescheduling}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) return;
                    const [y, m, d] = v.split("-").map(Number);
                    if (!y || !m || !d) return;
                    onReschedule(entry.id, new Date(Date.UTC(y, m - 1, d)));
                  }}
                  className="min-h-11 rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50"
                />
              </dd>
            </div>
          ) : (
            <DetailRow label="Date" value={dateLabel} />
          )}
          {/* Session SC5: ONE proposed line with one Accept, on an unassigned repair or technician visit and on an open call-out. The picker below is today's flow. */}
          {assignable && entry.assignedTo == null && (entry.type === "repair" || entry.type === "tech_visit") && onAcceptProposal && (
            <ProposedSlotLine
              target={{ jobId: entry.id }}
              busy={assigning}
              onAccept={(p) => (p.engineerId ? onAcceptProposal(entry.id, p.engineerId, p.scheduledFor ?? null) : undefined)}
            />
          )}
          {isOpenCallout && canDispatchCallout && (
            <ProposedSlotLine target={{ calloutId: entry.id }} busy={assigning} onAccept={(p) => (p.engineerId ? onAssignCallout(entry.id, p.engineerId) : undefined)} />
          )}
          {assignable ? (
            <div className="flex items-center justify-between gap-2 py-0.5">
              <dt className="text-ele-body">Engineer</dt>
              <dd>
                <select
                  aria-label="Engineer"
                  value={entry.assignedTo ?? ""}
                  disabled={assigning}
                  onChange={(e) => onAssign(entry.id, e.target.value || null)}
                  className="min-h-11 rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50"
                >
                  <option value="">Unassigned</option>
                  {orphanEngineerId && (
                    <option value={orphanEngineerId}>{`${engineerName.get(orphanEngineerId) ?? "Engineer"} (no longer listed)`}</option>
                  )}
                  {engineers.map((eng) => (
                    <option key={eng.id} value={eng.id}>
                      {eng.name}
                    </option>
                  ))}
                </select>
              </dd>
            </div>
          ) : isOpenCallout ? (
            <CalloutDispatchRow
              entry={entry}
              engineers={engineers}
              assigning={assigning}
              canDispatch={canDispatchCallout}
              onAssignCallout={onAssignCallout}
            />
          ) : (
            assignee && <DetailRow label="Engineer" value={assignee} />
          )}
          {/* Session PN1: ONE quiet line saying whether the engineer was told when the job was assigned. Amber only when they were not. */}
          {entry.kind === "job" && entry.engineerTold && (
            <p className={`pt-1 text-xs ${entry.engineerTold.warn ? "text-amber-400" : "text-ele-body opacity-80"}`} data-testid="engineer-told-line">
              {entry.engineerTold.text}
            </p>
          )}
          {assignBlockedReason && <p className="pt-1 text-xs text-ele-body opacity-80">{assignBlockedReason}</p>}
          {entry.kind === "job" && (
            <CrewRows
              entry={entry}
              engineers={engineers}
              engineerName={engineerName}
              editable={assignable && !!onChangeCrew}
              busy={assigning}
              onChange={(engineerId, action) => onChangeCrew?.(entry.id, engineerId, action)}
            />
          )}
          {/* Session BDM: ONE job over several working days. */}
          {entry.kind === "job" && entry.date && (assignable || entry.workDay) && (
            <JobDaysPanel jobId={entry.id} editable={assignable} engineers={engineers} onChanged={onDaysChanged} defaultOpen={!!entry.workDay} />
          )}
          <DetailRow label="Status" value={sentenceCaseStatus(entry.status)} />
          {/* Session RC1: a technician visit's time past the block is paid only when the office agreed it on the day. */}
          {entry.kind === "job" && entry.type === "tech_visit" && (
            <TechOvertimeControl jobId={entry.id} agreedAt={techOvertime.agreedAt} agreedByName={techOvertime.agreedByName} onChanged={setTechOvertime} />
          )}
          {/* Session ALW: acceptance, in the one place there is room to state it in full. BOTH
              states are shown here, unlike the card badge, which only flags the pending one: an
              office user who has opened a visit to decide what to do about it is asking exactly
              this question, and "Accepted" is a real answer to it. */}
          {acceptanceState(entry) === "pending" && <DetailRow label="Accepted" value="Not yet. The engineer has not confirmed this job." />}
          {acceptanceState(entry) === "accepted" && <DetailRow label="Accepted" value="Yes, the engineer has confirmed this job." />}
          {/* Session ALW: a site inspection's own reason. It is the only thing that says whether
              this is a pre-quote survey or an insurance check, and the two are not the same visit. */}
          {entry.inspectionReason && <DetailRow label="Reason" value={entry.inspectionReason} />}
          {entry.detail && <DetailRow label="Reported" value={entry.detail} />}
          {/* Session ALU: the same treatment assignBlockedReason gets above, for the same reason.
              A missing control with no explanation reads as a broken feature. */}
          {recordPending && <p className="pt-1 text-xs text-ele-body opacity-80">{recordPending}</p>}
        </dl>
        {assignError && <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{assignError}</p>}
        {rescheduleError && <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{rescheduleError}</p>}
        {pauseResumeError && <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{pauseResumeError}</p>}
        {error && <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{error}</p>}
        {/* Session AQY (D-AQX-1), reasons Session SC9A: the required-reason step for putting a job on hold. One fixed list; Other
            needs a few words. Nothing is sent to anybody by putting a job on hold. */}
        {pausing && (
          <div className="mt-3 rounded-ele border border-ele-purple-accent bg-ele-field p-3">
            <label htmlFor="pause-reason-kind" className="mb-1 block text-xs text-ele-body">
              Why is this job on hold?
            </label>
            <select
              id="pause-reason-kind"
              value={pauseHoldReason}
              onChange={(e) => setPauseHoldReason(e.target.value as JobHoldReasonKey)}
              disabled={pauseResumeBusy}
              className="min-h-11 w-full rounded-ele border border-ele-purple-accent bg-ele-bg px-2 py-1 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50"
            >
              {JOB_HOLD_REASON_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {pauseHoldReason === "other" && (
              <>
                <label htmlFor="pause-reason" className="mb-1 mt-2 block text-xs text-ele-body">
                  Say why in a few words
                </label>
                <input
                  id="pause-reason"
                  type="text"
                  autoFocus
                  maxLength={500}
                  value={pauseReason}
                  onChange={(e) => setPauseReason(e.target.value)}
                  placeholder="e.g. Key is with the porter, back Monday"
                  disabled={pauseResumeBusy}
                  className="min-h-11 w-full rounded-ele border border-ele-purple-accent bg-ele-bg px-2 py-1 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50"
                />
              </>
            )}
            <div className="mt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setPausing(false);
                  setPauseReason("");
                  setPauseHoldReason("awaiting_parts");
                }}
                disabled={pauseResumeBusy}
                className="rounded-ele border border-ele-purple-accent px-3 py-1.5 text-xs disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => onPause(entry.id, pauseHoldReason, pauseHoldReason === "other" ? pauseReason : "")}
                disabled={pauseResumeBusy || (pauseHoldReason === "other" && !pauseReason.trim())}
                className="rounded-ele border border-amber-400 px-3 py-1.5 font-heading text-xs tracking-wide text-amber-400 transition hover:bg-ele-inset disabled:opacity-50"
              >
                {pauseResumeBusy ? "Saving..." : "Confirm hold"}
              </button>
            </div>
          </div>
        )}
        <div className={`ele-touch-row mt-5 gap-3 sm:items-center ${MODAL_ACTIONS}`}>
          {paused && (
            <ConfirmButton
              onConfirm={() => onResume(entry.id)}
              label="Resume"
              busy={pauseResumeBusy}
              busyLabel="Resuming..."
              title="Moves this job back to in progress and clears the hold reason."
              className={buttonClasses("secondary", "md", MODAL_ACTION_BTN)}
            />
          )}
          {pausable && !pausing && (
            <button
              type="button"
              onClick={() => setPausing(true)}
              disabled={busy || pauseResumeBusy}
              className={`rounded-ele border border-amber-400 px-4 py-2 font-heading text-sm tracking-wide text-amber-400 transition hover:bg-ele-inset disabled:opacity-50 ${MODAL_ACTION_BTN}`}
            >
              Put on hold
            </button>
          )}
          {cancellable && !pausing && (
            <Button
              variant="destructive"
              onClick={onCancelJob}
              disabled={busy || pauseResumeBusy}
              title="Keeps the visit in this lift's history, marked as cancelled, unlike Delete, which removes it entirely."
              className={MODAL_ACTION_BTN}
            >
              Cancel visit
            </Button>
          )}
          {backdatable && onRecordBackdated && (
            <Button variant="secondary" onClick={onRecordBackdated} disabled={busy} className={MODAL_ACTION_BTN}>
              Record as completed
            </Button>
          )}
          {recordHref ? (
            // Session ALS: the link is inline-flex so the ele-touch-row 44px floor on the footer actually
            // applies (min-height does nothing to a plain inline <a>); ButtonLink is inline-flex.
            <ButtonLink to={recordHref} variant="secondary" className={MODAL_ACTION_BTN}>
              Open service record
            </ButtonLink>
          ) : deletable ? (
            <ConfirmButton
              onConfirm={onDelete}
              label="Delete visit"
              busy={busy}
              className={buttonClasses("destructive", "md", MODAL_ACTION_BTN)}
            />
          ) : entry.kind === "project" ? (
            // Session ARA (S-8): a real deep link, replacing the plain "Manage a project from the
            // Projects page" text this branch used to show. entry.id IS the project's own Prisma id
            // here (projectToEntry: `id: p.id`), never a re-derived value, so no server change was
            // needed to build it. Styled in the project's own card colour (border-ele-loler,
            // entryStyle's `kind === "project"` case above) rather than the green record link.
            <Link
              to={`/office/projects/${encodeURIComponent(entry.id)}`}
              className={`inline-flex items-center justify-center rounded-ele border border-ele-loler px-4 py-2 font-heading text-sm tracking-wide text-ele-loler transition hover:bg-ele-inset ${MODAL_ACTION_BTN}`}
            >
              Open project
            </Link>
          ) : (
            <span className="sm:mr-auto text-xs text-ele-body opacity-70">
              {entry.kind === "callout"
                ? "Charge, complete or cancel this call-out from the Call-outs page."
                : "Only an unstarted scheduled visit can be deleted."}
            </span>
          )}
          <button onClick={onClose} disabled={busy} className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm disabled:opacity-50 ${MODAL_ACTION_BTN}`}>
            Close
          </button>
        </div>
    </ModalShell>
  );
}

// The engineer row for an OPEN call-out (Session ALS). Two states, and the difference is a real
// server permission, not a UI preference:
//
//   ADMIN sees the same dropdown a job gets, listing the same active engineers. Choosing one calls
//   PATCH /api/callouts/:id/assign, which records the engineer AND creates the dispatch job, so the
//   call-out leaves this panel and lands on today's schedule.
//
//   OFFICE sees a sentence. That route is adminGuard'd (callout.routes.ts), so a dropdown here would
//   fail on every use -- the exact "shows a dropdown that fails silently on selection" this session
//   was told not to build. It names the alternative rather than just refusing.
function CalloutDispatchRow({
  entry,
  engineers,
  assigning,
  canDispatch,
  onAssignCallout,
}: {
  entry: CalEntry;
  engineers: { id: string; name: string }[];
  assigning: boolean;
  canDispatch: boolean;
  onAssignCallout: (calloutId: string, engineerId: string) => void;
}) {
  if (!canDispatch) {
    // Session ARA (S-7), D57: dispatch opened from admin-only to admin AND office, and this office
    // desktop tree is itself gated to those same two roles (App.tsx's ProtectedRoute), so this
    // branch is defensive rather than a live case today -- kept, with its wording corrected, rather
    // than removed, in case a future role ever reaches this component some other way.
    return (
      <div className="py-0.5">
        <dt className="text-ele-body">Engineer</dt>
        <dd className="mt-1 text-xs text-ele-body">
          Not yet assigned. Sending an engineer to a new call-out needs an admin or office account.
          Open this call-out from the Call-outs page, or ask someone who holds one.
        </dd>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <dt className="text-ele-body">Engineer</dt>
      <dd>
        <select
          aria-label="Engineer"
          value=""
          disabled={assigning}
          onChange={(e) => {
            if (e.target.value) onAssignCallout(entry.id, e.target.value);
          }}
          className="min-h-11 rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50"
        >
          {/* No "Unassigned" option: this call-out IS unassigned, and there is nothing to unassign
              it from. The placeholder is the current state, and picking a name is the only move. */}
          <option value="">Not yet assigned</option>
          {engineers.map((eng) => (
            <option key={eng.id} value={eng.id}>
              {eng.name}
            </option>
          ))}
        </select>
      </dd>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ele-body opacity-70">{label}</dt>
      <dd className="text-right text-ele-strong">{value}</dd>
    </div>
  );
}

// How long a piece of undated work has been waiting. Whole days, in the office's own words, because
// "raised 3 days ago" is a decision prompt and a raw ISO timestamp is not.
function waitedFor(iso: string | null | undefined, now: Date): string | null {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const days = Math.floor((startOfUtcDay(now).getTime() - startOfUtcDay(then).getTime()) / DAY_MS);
  if (days < 0) return null;
  if (days === 0) return "raised today";
  if (days === 1) return "waiting 1 day";
  return `waiting ${days} days`;
}

// ---- Standby banner (Session ARA, S-9) -----------------------------------------------------------
//
// Who is on call, for the week(s) the current view spans. A resolved standby week is not a day-cell
// entry: StandbyShift.weekStarting is @@unique per tenant per WEEK, never per day, and the rota it
// falls back to (when there is no office override for that exact week) is not a stored answer at all
// -- deriving it live (RULE 6) is the whole reason StandbyRota exists as a pattern rather than 52
// materialised rows a year. So this renders once, above the calendar grid, one line per week, rather
// than trying to draw a genuine multi-column span across a grid whose day columns and week rows it
// does not own (WeekView/MonthView's day cells; the engineer-by-week board is a sibling session's own
// work). Read-only throughout: standby is set from Settings -> Standby rota, never from here.
function StandbyBannerPanel({ weeks }: { weeks: CalendarStandbyWeek[] }) {
  if (weeks.length === 0) return null;
  return (
    <div data-testid="standby-banner" className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
      <h2 className="mb-1.5 font-heading text-base text-ele-blue">Standby</h2>
      <div className="space-y-1">
        {weeks.map((w) => (
          <p key={w.weekStarting} className="text-xs text-ele-body">
            <span className="text-ele-strong">{fmtWeekRange(new Date(`${w.weekStarting}T00:00:00.000Z`))}</span>
            {": "}
            {w.engineerName ? <span className="text-ele-blue">{w.engineerName}</span> : <span className="italic opacity-70">nobody on standby</span>}
            {w.reason && <span className="opacity-70"> ({w.reason})</span>}
          </p>
        ))}
      </div>
    </div>
  );
}

// ---- An undated SERVICE visit needs attention (Session ARA, S-15) -------------------------------
//
// Deliberately a SEPARATE panel from NeedsSchedulingPanel just below, not a fourth entry kind folded
// into it. UNSCHEDULED_JOB_TYPES (schedulingApi.routes.ts) has always deliberately excluded "service"
// with its own header comment explaining why: a PPM visit is always auto-generated WITH a date (the
// PPM anchor rule), so one with none means something went wrong upstream -- a bad anchor read, a
// generation bug, a hand-edited row -- and is not ordinary work waiting to be planned in. Folding it
// into the amber "Needs scheduling" list below would erase exactly that distinction and read as
// routine backlog. So this renders its OWN, visibly alarmed (red/danger, never amber) panel, worded so
// the office understands this is unusual rather than routine, ABOVE the ordinary panel: the more
// urgent signal reads first.
//
// UNCONDITIONAL on the type/engineer filters, on purpose: entries here are never fed through
// matchesType/matchesEngineer (they are not sourced from `entries`/`allEntries` at all -- see the
// dedicated data.unscheduled.servicesNeedingAttention key and its own render-site guard). An alarm
// about a data problem should not disappear because the office happened to have the Call-out type
// filter selected; that would defeat the point of surfacing it distinctly in the first place.
//
// Still built on the SAME shared EntryCard/jobToEntry machinery as every other job card on this page
// (mapped locally, per render, purely for field access -- never inserted into entriesByDay/allEntries),
// so opening one offers the SAME popover, the SAME engineer/date picker, as any other undated job: the
// office can act on the alarm immediately, not just read it.
function ServiceNeedsAttentionPanel({
  jobs,
  engineerName,
  drag,
  capped = false,
  shownLimit,
}: {
  jobs: DispatchJob[];
  engineerName: Map<string, string>;
  drag: DragApi;
  capped?: boolean;
  shownLimit?: number;
}) {
  const now = new Date();
  const entries = jobs.map(jobToEntry);
  const ordered = [...entries].sort((a, b) => {
    const at = a.waitingSince ? new Date(a.waitingSince).getTime() : Number.POSITIVE_INFINITY;
    const bt = b.waitingSince ? new Date(b.waitingSince).getTime() : Number.POSITIVE_INFINITY;
    return at - bt;
  });
  if (ordered.length === 0) return null;
  return (
    <div data-testid="services-needing-attention" className="mb-4 rounded-ele border border-ele-danger bg-ele-surface p-3">
      <h2 className="mb-1.5 font-heading text-base text-ele-danger">Service visits with no date: needs attention ({ordered.length})</h2>
      <p className="mb-3 text-xs text-ele-body">
        A maintenance visit is always generated with a date. One with none means something went wrong
        upstream, not ordinary backlog. Open it to see why and give it a date.
        {capped && shownLimit
          ? ` More than ${shownLimit} are waiting: this is the longest-waiting ${shownLimit}, not the whole list.`
          : ""}
      </p>
      <div className="space-y-2">
        {ordered.map((e) => {
          const waited = waitedFor(e.waitingSince, now);
          return (
            <div key={entryKey(e)} className="[&>div]:min-h-[60px] [&>div]:p-3">
              <EntryCard entry={e} engineerName={engineerName} drag={drag} dragHint="open to give it a date" subtitle={waited} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Work that needs scheduling (Session ALS) --------------------------------------------------
//
// The panel that closes the Director's actual gap. Everything here has no date: an OPEN call-out
// waiting for its first engineer, and a REPAIR raised with no date (both creation paths make one
// deliberately). Neither could reach a day cell, so neither could be tapped, so two of the three job
// types he named could not be assigned from this page at all -- not on a phone, and not on a desktop
// either. The interaction itself was never the problem.
//
// The cards are the SAME EntryCard the calendar uses, so tapping one opens the SAME popover with the
// SAME engineer dropdown. There is deliberately no second assign control here: one interaction, one
// implementation, whichever surface the work is standing on.
function NeedsSchedulingPanel({
  entries,
  engineerName,
  drag,
  capped = false,
  shownLimit,
}: {
  entries: CalEntry[];
  engineerName: Map<string, string>;
  drag: DragApi;
  // The feed bounds each list. A bounded list that looks complete is worse than one that says it is
  // bounded, so the panel states it rather than letting the office read a cap as a total.
  capped?: boolean;
  shownLimit?: number;
}) {
  const now = new Date();
  // Urgent first (an entrapment or an emergency intake is not "when you get to it"), then longest
  // waiting, so the top of the list is always the next decision to take.
  const ordered = [...entries].sort((a, b) => {
    if ((a.urgent === true) !== (b.urgent === true)) return a.urgent === true ? -1 : 1;
    const at = a.waitingSince ? new Date(a.waitingSince).getTime() : Number.POSITIVE_INFINITY;
    const bt = b.waitingSince ? new Date(b.waitingSince).getTime() : Number.POSITIVE_INFINITY;
    return at - bt;
  });
  return (
    <div data-testid="needs-scheduling" className="mb-4 rounded-ele border border-amber-400 bg-ele-surface p-3">
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <h2 className="font-heading text-base text-amber-400">Needs scheduling ({ordered.length})</h2>
      </div>
      <p className="mb-3 text-xs text-ele-body">
        Work with no date yet, so it appears on no day. Open one to give it an engineer and a date.
        {capped && shownLimit
          ? ` More than ${shownLimit} are waiting: this is the longest-waiting ${shownLimit}, not the whole list.`
          : ""}
      </p>
      <div className="space-y-2">
        {ordered.map((e) => {
          const waited = waitedFor(e.waitingSince, now);
          return (
            <div key={entryKey(e)} className="[&>div]:min-h-[60px] [&>div]:p-3">
              <EntryCard
                entry={e}
                engineerName={engineerName}
                drag={drag}
                dragHint="open to assign an engineer"
                subtitle={[e.urgent === true ? "Urgent" : null, waited].filter(Boolean).join(" · ") || null}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Session AGY: the single-day view -- a full-width card list, the phone-first way to read the
// schedule (the week/month grids scroll horizontally by design). Session AJ adds engineer lanes:
// this is now also where a job's engineer is assigned, reassigned or unassigned, by dragging its
// card into a lane (or the Unassigned lane). This replaces the engineer-lane dispatch board
// (DispatchBoard.tsx), retired and unrouted since Session O, whose drag-to-assign never had a
// living replacement. Day-to-day rescheduling stays on the week/month grids. Prev/Next page a day
// at a time.
function DayView({
  day,
  entriesByDay,
  trainingByDay,
  engineerName,
  engineers,
  todayKey,
  drag,
  onBulkAssign,
  bulkAssignBusy,
}: {
  day: Date;
  entriesByDay: Map<string, CalEntry[]>;
  // Session ARA (S-9): a separate map, deliberately never merged into entriesByDay -- see
  // TrainingSessionMarker's own note on why a training session is not a CalEntry.
  trainingByDay: Map<string, CalendarTrainingSession[]>;
  engineerName: Map<string, string>;
  engineers: { id: string; name: string }[];
  todayKey: string;
  drag: DragApi;
  // Session AQS, S-3: assign every checked entry in the Unassigned lane to one engineer at once.
  onBulkAssign: (jobIds: string[], engineerId: string) => void;
  bulkAssignBusy: boolean;
}) {
  const k = keyOf(day);
  const dayEntries = entriesByDay.get(k) ?? [];
  const dayTraining = trainingByDay.get(k) ?? [];
  const isToday = k === todayKey;
  const projectEntries = dayEntries.filter((e) => e.kind === "project");
  const jobEntries = dayEntries.filter((e) => e.kind === "job");
  // Every active engineer gets a lane, even with nothing in it today, so an unassigned (or
  // wrongly-assigned) visit always has somewhere to be dragged to.
  const laneIds = new Set(engineers.map((eng) => eng.id));
  // Session ALE: every visit must come OUT of exactly one lane. This used to be a partition with a
  // hole -- a job whose assignedTo names an engineer who is not in this list matched neither the
  // "unassigned" filter nor any engineer filter, so it rendered NOWHERE, with no count and no
  // warning, and the office read the day as if it did not exist. Meanwhile it is still on that
  // engineer's phone (the ENGINEER PWA VISIBILITY RULE scopes by assignedTo, not by this list).
  // A deactivated engineer, or one the engineers endpoint does not return, is enough to trigger it.
  const orphaned = jobEntries.filter((e) => e.assignedTo != null && !laneIds.has(e.assignedTo));
  const lanes: { key: string; label: string; entries: CalEntry[] }[] = [
    { key: "unassigned", label: "Unassigned", entries: jobEntries.filter((e) => e.assignedTo == null) },
    ...engineers.map((eng) => ({ key: eng.id, label: eng.name, entries: jobEntries.filter((e) => e.assignedTo === eng.id) })),
  ];
  return (
    <div data-testid="day-view" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
      <div className={`mb-2 font-menu text-xs uppercase tracking-wider ${isToday ? "text-ele-green" : "text-ele-body"}`}>
        {formatWeekdayShort(day)}
        {isToday ? " (today)" : ""}
      </div>
      {/* Session ARA (S-9): training sessions render above the dispatch lanes, unconditionally --
          they are not part of dayEntries/jobEntries/projectEntries below and never gate the "Nothing
          scheduled" fallback on their own (a training-only day still says so about JOBS, which is
          what that message has always meant; see the combined empty check just below). */}
      {dayTraining.length > 0 && (
        <div className="mb-3 space-y-1.5">
          {dayTraining.map((s) => (
            <TrainingSessionMarker key={`training:${s.id}`} session={s} />
          ))}
        </div>
      )}
      {dayEntries.length === 0 && dayTraining.length === 0 ? (
        <p className="py-6 text-center text-sm text-ele-body">Nothing scheduled for this day.</p>
      ) : dayEntries.length === 0 ? null : (
        <>
          {projectEntries.length > 0 && (
            <div className="mb-3 space-y-2">
              {projectEntries.map((e) => (
                <DayEntry key={entryKey(e)} entry={e} engineerName={engineerName} drag={drag} todayKey={todayKey} />
              ))}
            </div>
          )}
          <div className="space-y-3">
            {lanes.map((lane) => (
              <EngineerLane
                key={lane.key}
                laneKey={lane.key}
                label={lane.label}
                entries={lane.entries}
                engineerName={engineerName}
                drag={drag}
                engineers={lane.key === "unassigned" ? engineers : undefined}
                onBulkAssign={lane.key === "unassigned" ? onBulkAssign : undefined}
                bulkAssignBusy={bulkAssignBusy}
                todayKey={todayKey}
              />
            ))}
          </div>
          {/* Session ALE: a BLOCK, deliberately not a lane -- nothing can be dropped into it (there
              is no engineer to drop onto) and the lane list keeps exactly the shape its tests pin.
              Reassigning from here is the ordinary route: tap the visit, pick a real engineer. */}
          {orphaned.length > 0 && (
            <div data-testid="orphan-block" className="mt-3 rounded-ele border border-amber-400 p-2">
              <div className="mb-1.5 font-menu max-md:text-xs text-[11px] uppercase tracking-wider text-amber-400">
                Assigned to an engineer who is no longer listed
              </div>
              <p className="mb-2 text-xs text-ele-body">
                Open each visit and pick a current engineer. Until then it stays on the original
                engineer&rsquo;s phone.
              </p>
              <div className="space-y-2">
                {orphaned.map((e) => (
                  <DayEntry key={entryKey(e)} entry={e} engineerName={engineerName} drag={drag} todayKey={todayKey} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// One engineer's lane (or the Unassigned lane) for the day view. A drop here reassigns the
// dragged job to this engineer (null for Unassigned) via drag.onDropLane; the day and time are
// untouched. Highlights while a compatible drag hovers over it.
function EngineerLane({
  laneKey,
  label,
  entries,
  engineerName,
  drag,
  engineers,
  onBulkAssign,
  bulkAssignBusy,
  todayKey,
}: {
  laneKey: string;
  label: string;
  entries: CalEntry[];
  engineerName: Map<string, string>;
  drag: DragApi;
  // Session AQS, S-3: only ever passed for the Unassigned lane (the caller decides). undefined
  // everywhere else, which is what keeps every other lane's render byte-identical to before.
  engineers?: { id: string; name: string }[];
  onBulkAssign?: (jobIds: string[], engineerId: string) => void;
  bulkAssignBusy?: boolean;
  // Session ARA (S-10): threaded through to EntryCard's Overdue badge (via DayEntry, below).
  todayKey?: string;
}) {
  const engineerId = laneKey === "unassigned" ? null : laneKey;
  const over = drag.overLane === laneKey;
  const canBulkAssign = laneKey === "unassigned" && Boolean(onBulkAssign) && entries.length > 1;
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkEngineer, setBulkEngineer] = useState("");

  function toggleSelected(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function exitSelectMode() {
    setSelectMode(false);
    setSelected(new Set());
    setBulkEngineer("");
  }

  return (
    <div
      data-testid="engineer-lane"
      data-lane={laneKey}
      onDragOver={(e) => {
        e.preventDefault();
        drag.onLaneOver(laneKey);
      }}
      onDragLeave={() => drag.onLaneLeave(laneKey)}
      onDrop={(e) => {
        e.preventDefault();
        drag.onDropLane(engineerId);
      }}
      className={`rounded-ele border border-ele-purple-accent p-2 transition ${over ? "bg-ele-inset-strong" : "bg-ele-inset"}`}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className="font-menu max-md:text-xs text-[11px] uppercase tracking-wider text-ele-blue">{label}</div>
        {canBulkAssign && !selectMode && (
          <button
            type="button"
            onClick={() => setSelectMode(true)}
            className="font-menu max-md:text-xs text-[11px] tracking-wide text-ele-body underline-offset-2 hover:text-ele-green hover:underline"
          >
            Select multiple
          </button>
        )}
        {selectMode && (
          <button type="button" onClick={exitSelectMode} className="font-menu max-md:text-xs text-[11px] tracking-wide text-ele-body hover:text-ele-strong">
            Cancel
          </button>
        )}
      </div>
      {entries.length === 0 ? (
        <p className="py-2 text-center text-xs text-ele-body opacity-60">
          {/* Session ALE: on a touchscreen there is no drag to offer, so the lane states what it is
              rather than asking for a gesture the device cannot perform. The touch route to an
              engineer is the entry popover: tap a visit, pick the engineer. The fine-pointer
              wording is byte-identical to what it has always been. */}
          {!drag.coarsePointer
            ? "Drag a visit here to assign."
            : engineerId === null
              ? "Every visit today has an engineer."
              : "Nothing assigned. Tap a visit to set its engineer."}
        </p>
      ) : (
        <div className="space-y-2">
          {entries.map((e) =>
            selectMode ? (
              <label key={entryKey(e)} className="flex min-h-11 items-center gap-2">
                <input
                  type="checkbox"
                  checked={selected.has(e.id)}
                  onChange={() => toggleSelected(e.id)}
                  className="h-4 w-4 flex-none accent-ele-green"
                  aria-label={`Select ${e.reference ?? e.ref ?? e.siteName}`}
                />
                <span className="min-w-0 flex-1 [&>div]:pointer-events-none">
                  <DayEntry entry={e} engineerName={engineerName} drag={drag} todayKey={todayKey} />
                </span>
              </label>
            ) : (
              <DayEntry key={entryKey(e)} entry={e} engineerName={engineerName} drag={drag} todayKey={todayKey} />
            ),
          )}
        </div>
      )}
      {selectMode && (
        <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-ele-purple-accent pt-2">
          <select
            value={bulkEngineer}
            onChange={(e) => setBulkEngineer(e.target.value)}
            aria-label="Assign selected to"
            className="min-h-11 rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-xs text-ele-strong outline-none focus:border-ele-green"
          >
            <option value="">Assign selected to...</option>
            {(engineers ?? []).map((eng) => (
              <option key={eng.id} value={eng.id}>
                {eng.name}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            disabled={selected.size === 0 || !bulkEngineer || bulkAssignBusy}
            onClick={() => {
              const ids = Array.from(selected);
              onBulkAssign?.(ids, bulkEngineer);
              exitSelectMode();
            }}
            className="min-h-11"
          >
            {bulkAssignBusy ? "Assigning..." : `Assign ${selected.size || ""}`}
          </Button>
        </div>
      )}
    </div>
  );
}

// A day-view row: the shared EntryCard inside a comfortable full-width tap target. Draggable
// (into another engineer lane) whenever the underlying job is reassignable; the "kind === job"
// check in EntryCard already keeps a project card from offering to drag.
//
// Session ALU adds the closed loop the Director asked for: a visit that HAS a service record links
// straight to it from the row, instead of only from inside the popover the row opens. Three
// deliberate choices behind that one link:
//   - It lives HERE, in the day-only wrapper, not in EntryCard. EntryCard is shared by the Week
//     and Month cells and the Needs-scheduling panel, where a card is a few centimetres wide and
//     an extra line would not fit; the brief asked for the Day view specifically.
//   - It is a SIBLING of the card, never nested inside it. The card is a role="button" whose whole
//     surface opens the popover and which sets dataTransfer on drag: an <a> inside it would be an
//     interactive element inside a button, would bubble every click into the popover, and is
//     natively draggable so it could hijack the drag-to-assign gesture. draggable={false} closes
//     the last of those even as a sibling.
//   - It renders an <a>, not a <div>, which is what keeps the wrapper's `[&>div]` size rules
//     (min-h-[60px], p-3) pointed at the card alone. A <div> here would silently inflate to a
//     60px padded block.
// A visit with no record yet gets nothing here, and is told why in the popover (recordPendingNote):
// a row is the wrong place for a paragraph, and a disabled-looking link would be worse than none.
function DayEntry({
  entry,
  engineerName,
  drag,
  todayKey,
}: {
  entry: CalEntry;
  engineerName: Map<string, string>;
  drag: DragApi;
  // Session ARA (S-10): threaded through to EntryCard's Overdue badge.
  todayKey?: string;
}) {
  const recordHref = serviceRecordHrefFor(entry);
  return (
    <div className="[&>div]:min-h-[60px] [&>div]:p-3">
      <EntryCard entry={entry} engineerName={engineerName} drag={drag} dragHint="drag to assign an engineer" todayKey={todayKey} />
      {/* Session ARA (S-9): a lone-worker indicator, ON the day row alongside the card rather than
          inside EntryCard -- the same placement reasoning as the Service record link just below (a
          week/month card is a few centimetres wide and has no room for a third line). A SIBLING
          element, never nested inside the card's own role="button" surface, so it cannot be dragged
          or swallow the card's click. */}
      {entry.kind === "job" && entry.loneWorkerCheckedIn && (
        <p
          className={`mt-1 flex items-center gap-1 px-1 font-menu max-md:text-xs text-[11px] tracking-wide ${
            entry.loneWorkerOverdue ? "text-ele-danger" : "text-ele-blue"
          }`}
        >
          {entry.loneWorkerOverdue ? "Lone worker: check-in overdue" : "Lone worker checked in on site"}
        </p>
      )}
      {recordHref && (
        <Link
          to={recordHref}
          draggable={false}
          // Full-width flex so the tap target is the row, not the two words in it; max-sm:min-h-11
          // holds the 44px touch floor the day view is built to (Sessions ALE / ALS).
          className="mt-1 flex items-center justify-end px-1 py-1 font-menu max-md:text-xs text-[11px] tracking-wide text-ele-green underline-offset-2 transition hover:underline max-sm:min-h-11"
        >
          Service record &#8594;
        </Link>
      )}
    </div>
  );
}

// ---- Session ARA (S-9): a training session, as a day-cell overlay -------------------------------
//
// Deliberately NOT the shared EntryCard: a training session is read-only calendar information, never
// dispatchable work (no assign route reads it, no drag target could honestly write anywhere), and
// EntryCard/CalEntry carry a lot of job/project machinery (drag eligibility, the assign popover,
// pause/cancel) that would either have to grow a third "kind" through every one of those branches or
// silently misbehave for one it does not recognise. A plain Link to the real record instead: it opens
// the SAME Training pages a click from the Training register would, never a drag target, never a
// click-to-popover, and never counted in entriesByDay/matchesType/matchesEngineer.
function TrainingSessionMarker({ session }: { session: CalendarTrainingSession }) {
  return (
    <Link
      to={`/office/training/${session.id}`}
      draggable={false}
      title={`Training: ${session.typeLabel} at ${session.siteName || "site TBC"}${session.engineerName ? ` (${session.engineerName})` : ""}`}
      className="block min-w-0 rounded-ele border border-dashed border-ele-blue bg-ele-inset px-1.5 py-1 max-md:text-xs text-[10px] leading-tight text-ele-body no-underline transition hover:border-ele-green"
    >
      <span className="font-menu uppercase tracking-wide text-ele-blue">Training</span>
      <div className="truncate text-ele-strong">{session.typeLabel}</div>
    </Link>
  );
}

// ---- Session ARA (S-13): a project's span, as an interim treatment ------------------------------
//
// A Project has startDate and targetCompletion but no `endDate` column, so this is a DTO-and-
// rendering change, never a migration (a real multi-day job/visit model, S-12, is a separately
// gated later phase). ProjectSpan is derived ONCE from the raw project list (independent of the
// view/cursor), so every day cell's lookup is a cheap map read rather than a per-render scan.
//
// A span is only formed when BOTH dates are known and genuinely differ: a project with only one
// date (or none) keeps rendering exactly as it always has, as a single EntryCard marker on its one
// placement day (`projectToEntry`/`entriesByDay`, untouched by this change) -- "a fine fallback when
// only one date is known", as the brief for this item put it. Deliberately kept OUT of CalEntry: the
// existing single-day marker is not being replaced, only ADDED to, with a second, purely visual
// layer showing the days in between.
interface ProjectSpan {
  id: string;
  ref: string;
  title: string;
  siteName: string;
  start: Date; // startOfUtcDay
  end: Date; // startOfUtcDay, strictly after start
}

function projectSpans(projects: CalendarProject[]): ProjectSpan[] {
  const spans: ProjectSpan[] = [];
  for (const p of projects) {
    if (!p.startDate || !p.targetCompletion) continue;
    const start = startOfUtcDay(new Date(p.startDate));
    const end = startOfUtcDay(new Date(p.targetCompletion));
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue;
    if (end.getTime() <= start.getTime()) continue; // same day, or an inverted pair: no span to draw
    spans.push({ id: p.id, ref: p.ref, title: p.title, siteName: p.siteName, start, end });
  }
  return spans;
}

function spansForDay(spans: ProjectSpan[], day: Date): (ProjectSpan & { isStart: boolean; isEnd: boolean })[] {
  const t = day.getTime();
  return spans
    .filter((s) => t >= s.start.getTime() && t <= s.end.getTime())
    .map((s) => ({ ...s, isStart: t === s.start.getTime(), isEnd: t === s.end.getTime() }));
}

// A thin coloured strip, one per covered day, that reads as a single continuous bar once adjacent
// days each render their own segment: rounded only on the genuine start/end day, and pulled flush
// into its neighbour on the other side with a negative margin so there is no visible gap between
// segments. Reuses the project's own locked colour (bg-ele-loler, the same token entryStyle already
// gives a project's border/text) rather than inventing a new one.
//
// The week column and the month cell both pull 6px (-ml-1.5), the month cell's pre-BDU value kept so the
// desktop strip still bridges the 1px column border; a 2px poke into the neighbouring cell is the bridge.
// The stacked phone week has no neighbouring column to join, so it draws the strip with no pull at all.
type SpanBleed = "week" | "month" | "none";
const SPAN_BLEED_START: Record<SpanBleed, string> = { week: "-ml-1.5", month: "-ml-1.5", none: "" };
const SPAN_BLEED_END: Record<SpanBleed, string> = { week: "-mr-1.5", month: "-mr-1.5", none: "" };
function ProjectSpanStrip({ span, bleed = "week" }: { span: ProjectSpan & { isStart: boolean; isEnd: boolean }; bleed?: SpanBleed }) {
  return (
    <div
      title={`${span.ref}: ${span.title}${span.siteName ? ` (${span.siteName})` : ""}`}
      className={`h-1.5 min-w-0 bg-ele-loler ${span.isStart || bleed === "none" ? "rounded-l-full" : SPAN_BLEED_START[bleed]} ${span.isEnd || bleed === "none" ? "rounded-r-full" : SPAN_BLEED_END[bleed]}`}
    />
  );
}

function WeekView({
  days,
  entriesByDay,
  trainingByDay,
  projectSpans: spans,
  engineerName,
  todayKey,
  drag,
}: {
  days: Date[];
  entriesByDay: Map<string, CalEntry[]>;
  // Session ARA (S-9): a separate map, deliberately never merged into entriesByDay -- see
  // TrainingSessionMarker's own note on why a training session is not a CalEntry.
  trainingByDay: Map<string, CalendarTrainingSession[]>;
  // Session ARA (S-13): the project spans with a genuine two-date range, computed once from the raw
  // project list (see projectSpans above). Renders alongside, never instead of, the existing
  // single-day EntryCard marker entriesByDay already carries for a project.
  projectSpans: ProjectSpan[];
  engineerName: Map<string, string>;
  todayKey: string;
  drag: DragApi;
}) {
  // A phone gets the week as seven stacked day sections at full width instead of the seven-column
  // grid (110px columns squeezed every card to a word a line, and the day headers scrolled away with
  // their columns). Decided ONCE AT MOUNT, exactly as the page chooses its opening view and as
  // initialMobileLayout documents: exactly one structure is ever in the DOM, so a card never exists
  // twice and a phone rotating across the md boundary keeps what it mounted. jsdom has no matchMedia,
  // so every test that does not stub it renders the grid it was written against.
  const [stacked] = useState(initialMobileLayout);
  if (stacked) {
    return (
      <div data-testid="week-stack" className="space-y-3">
        {days.map((d) => {
          const k = keyOf(d);
          const isToday = k === todayKey;
          const dayEntries = entriesByDay.get(k) ?? [];
          const dayTraining = trainingByDay.get(k) ?? [];
          const daySpans = spansForDay(spans, d);
          const empty = dayEntries.length === 0 && dayTraining.length === 0;
          return (
            <section
              key={k}
              data-testid="week-day-section"
              data-day-section={k}
              className={`min-w-0 overflow-hidden rounded-ele border border-ele-purple-accent ${isToday ? "ring-1 ring-ele-green" : ""}`}
            >
              <h3
                className={`flex min-w-0 items-baseline justify-between gap-2 border-b border-ele-purple-accent bg-ele-inset px-3 py-2 font-menu text-xs uppercase tracking-wider ${isToday ? "text-ele-green" : "text-ele-blue"}`}
              >
                <span className="min-w-0 truncate">
                  {formatWeekdayShort(d).split(" ")[0]}
                  {isToday ? " (today)" : ""}
                </span>
                <span className="min-w-0 truncate normal-case tracking-normal opacity-70">{formatDateLong(d).split(" ").slice(1).join(" ")}</span>
              </h3>
              {daySpans.length > 0 && (
                <div data-testid="project-span-row" className="space-y-0.5 px-3 pt-2">
                  {daySpans.map((s) => (
                    <ProjectSpanStrip key={`span:${s.id}`} span={s} bleed="none" />
                  ))}
                </div>
              )}
              <div
                data-day={k}
                onDragOver={(e) => {
                  e.preventDefault();
                  drag.onCellOver(k);
                }}
                onDragLeave={() => drag.onCellLeave(k)}
                onDrop={(e) => {
                  e.preventDefault();
                  void drag.onDropDay(d);
                }}
                className={`min-w-0 space-y-2 p-2 transition ${drag.overCell === k ? "bg-ele-inset-strong" : ""}`}
              >
                {dayTraining.map((s) => (
                  <TrainingSessionMarker key={`training:${s.id}`} session={s} />
                ))}
                {dayEntries.map((e) => (
                  <EntryCard key={entryKey(e)} entry={e} engineerName={engineerName} drag={drag} todayKey={todayKey} />
                ))}
                {empty && <p className="px-1 py-1 text-xs text-ele-body opacity-60">Nothing booked</p>}
              </div>
            </section>
          );
        })}
      </div>
    );
  }
  return (
    <div data-hscroll className="overflow-x-auto rounded-ele border border-ele-purple-accent">
      <div className="grid min-w-[840px]" style={{ gridTemplateColumns: "repeat(7, minmax(110px, 1fr))" }}>
        {days.map((d) => {
          const k = keyOf(d);
          const isToday = k === todayKey;
          const dayEntries = entriesByDay.get(k) ?? [];
          const dayTraining = trainingByDay.get(k) ?? [];
          const daySpans = spansForDay(spans, d);
          return (
            <div key={k} data-testid="week-col" className={`min-w-0 border-l border-ele-purple-accent first:border-l-0 ${isToday ? "ring-1 ring-inset ring-ele-green" : ""}`}>
              <div className="min-w-0 break-words border-b border-ele-purple-accent bg-ele-inset px-2 py-2 text-center font-menu text-xs uppercase tracking-wider text-ele-blue">
                {formatWeekdayShort(d)}
                <span className="block truncate max-md:text-xs text-[10px] normal-case tracking-normal opacity-70">{formatDateLong(d).split(" ").pop()}</span>
              </div>
              {daySpans.length > 0 && (
                <div data-testid="project-span-row" className="space-y-0.5 px-1.5 pt-1.5">
                  {daySpans.map((s) => (
                    <ProjectSpanStrip key={`span:${s.id}`} span={s} />
                  ))}
                </div>
              )}
              <div
                data-day={k}
                onDragOver={(e) => {
                  e.preventDefault();
                  drag.onCellOver(k);
                }}
                onDragLeave={() => drag.onCellLeave(k)}
                onDrop={(e) => {
                  e.preventDefault();
                  void drag.onDropDay(d);
                }}
                className={`min-h-[120px] space-y-1.5 p-1.5 transition ${drag.overCell === k ? "bg-ele-inset-strong" : ""}`}
              >
                {dayTraining.map((s) => (
                  <TrainingSessionMarker key={`training:${s.id}`} session={s} />
                ))}
                {dayEntries.map((e) => (
                  <EntryCard key={entryKey(e)} entry={e} engineerName={engineerName} drag={drag} todayKey={todayKey} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Session ARA (S-1): the engineer-by-week dispatch board ------------------------------------
//
// One row per engineer (Unassigned first), one column per day of the week, Monday to Sunday: the
// shape the retired DispatchBoard.tsx (Session O, deleted Session AQS) had, rebuilt on this page's
// own CalEntry / DragApi architecture rather than resurrected. Every cell is a drop target, and a
// drop reschedules AND reassigns in one write (drag.onDropBoardCell, moveJobOnBoard); the Day view's
// single-day lanes and the week grid's day-only cells are unchanged beside it.
//
// The lane construction is DayView's, applied to a week instead of a day, and the two must keep
// partitioning the same way: Unassigned first, then EVERY active engineer (even one with nothing
// this week, so a visit always has somewhere to be dragged to), and every job that names an engineer
// NOT in that list surfaces in an amber row at the bottom rather than vanishing (the ALE orphan
// rule: it is still on that engineer's phone). That row is a BLOCK, not a lane: nothing can be
// dropped into it, because there is no engineer to drop onto; the way out is the ordinary one (open
// the visit and pick a current engineer, or drag it into a real row above).
//
// PROJECTS keep their own row above the engineer rows, matching how DayView keeps projectEntries out
// of the job lanes: a project is not engineer-assigned work and its card never drags. A project still
// renders as ONE marker on its placement day (startDate ?? targetCompletion, exactly as every other
// view shows it); making it span its date range is S-13, a later session's work, and this row is
// where that change lands without touching the engineer grid.
function EngineerWeekBoardView({
  days,
  entriesByDay,
  engineerName,
  engineers,
  todayKey,
  drag,
}: {
  days: Date[];
  entriesByDay: Map<string, CalEntry[]>;
  engineerName: Map<string, string>;
  engineers: { id: string; name: string }[];
  todayKey: string;
  drag: DragApi;
}) {
  const dayKeys = days.map(keyOf);
  const laneIds = new Set(engineers.map((eng) => eng.id));
  const onDay = (k: string) => entriesByDay.get(k) ?? [];
  const projectsOn = (k: string) => onDay(k).filter((e) => e.kind === "project");
  const jobsOn = (k: string, engineerId: string | null) => onDay(k).filter((e) => e.kind === "job" && (e.assignedTo ?? null) === engineerId);
  const orphansOn = (k: string) => onDay(k).filter((e) => e.kind === "job" && e.assignedTo != null && !laneIds.has(e.assignedTo));
  const hasProjects = dayKeys.some((k) => projectsOn(k).length > 0);
  const hasOrphans = dayKeys.some((k) => orphansOn(k).length > 0);
  const weekIsEmpty = dayKeys.every((k) => onDay(k).length === 0);
  const lanes: { key: string; engineerId: string | null; label: string }[] = [
    { key: "unassigned", engineerId: null, label: "Unassigned" },
    ...engineers.map((eng) => ({ key: eng.id, engineerId: eng.id, label: eng.name })),
  ];
  const columns = "150px repeat(7, minmax(120px, 1fr))";
  return (
    <div data-testid="engineer-week-board">
      <div data-hscroll className="overflow-x-auto rounded-ele border border-ele-purple-accent">
        <div className="min-w-[990px]">
          <div className="grid" style={{ gridTemplateColumns: columns }}>
            <div className="sticky left-0 z-10 border-b border-ele-purple-accent bg-ele-base">
              <div className="bg-ele-inset px-3 py-2 font-menu text-xs uppercase tracking-widest text-ele-blue">Engineer</div>
            </div>
            {days.map((d) => {
              const k = keyOf(d);
              const isToday = k === todayKey;
              return (
                <div
                  key={k}
                  data-testid="board-day-header"
                  className={`min-w-0 break-words border-b border-l border-ele-purple-accent bg-ele-inset px-2 py-2 text-center font-menu text-xs uppercase tracking-wider ${isToday ? "text-ele-green" : "text-ele-blue"}`}
                >
                  {formatWeekdayShort(d)}
                  {isToday ? " (today)" : ""}
                  <span className="block truncate max-md:text-xs text-[10px] normal-case tracking-normal opacity-70">{formatDateLong(d).split(" ").pop()}</span>
                </div>
              );
            })}
          </div>
          {hasProjects && (
            <div data-testid="board-projects-row" className="grid border-b border-ele-purple-accent" style={{ gridTemplateColumns: columns }}>
              <div className="sticky left-0 z-10 bg-ele-base px-3 py-3 font-menu max-md:text-xs text-[11px] uppercase tracking-wider text-ele-loler">Projects</div>
              {dayKeys.map((k) => (
                <div key={k} data-testid="board-project-cell" data-day={k} className="min-h-[64px] space-y-1.5 border-l border-ele-purple-accent p-1.5">
                  {projectsOn(k).map((e) => (
                    <EntryCard key={entryKey(e)} entry={e} engineerName={engineerName} drag={drag} compact todayKey={todayKey} />
                  ))}
                </div>
              ))}
            </div>
          )}
          {lanes.map((lane) => (
            <div key={lane.key} data-testid="board-lane" data-lane={lane.key} className="grid border-b border-ele-purple-accent" style={{ gridTemplateColumns: columns }}>
              <div className={`sticky left-0 z-10 bg-ele-base px-3 py-3 font-menu max-md:text-xs text-[11px] uppercase tracking-wider ${lane.engineerId ? "text-ele-blue" : "text-ele-body"}`}>{lane.label}</div>
              {days.map((d) => (
                <BoardCell key={keyOf(d)} laneKey={lane.key} engineerId={lane.engineerId} day={d} entries={jobsOn(keyOf(d), lane.engineerId)} engineerName={engineerName} drag={drag} todayKey={todayKey} />
              ))}
            </div>
          ))}
          {hasOrphans && (
            <div data-testid="board-orphan-row" className="grid border-b border-amber-400" style={{ gridTemplateColumns: columns }}>
              <div className="sticky left-0 z-10 bg-ele-base px-3 py-3 font-menu max-md:text-xs text-[11px] uppercase tracking-wider text-amber-400">No longer listed</div>
              {dayKeys.map((k) => (
                <div key={k} data-testid="board-orphan-cell" data-day={k} className="min-h-[64px] space-y-1.5 border-l border-ele-purple-accent p-1.5">
                  {/* Not compact: the row cannot name the engineer, so the card keeps its own line. */}
                  {orphansOn(k).map((e) => (
                    <EntryCard key={entryKey(e)} entry={e} engineerName={engineerName} drag={drag} dragHint="drag to another day or engineer" todayKey={todayKey} />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <p className="mt-2 text-xs text-ele-body">
        {weekIsEmpty ? "Nothing scheduled this week. " : ""}
        {/* Session ALE's rule, applied here as it is on the day view: a touchscreen is told the tap
            route rather than asked for a gesture it cannot perform. */}
        {drag.coarsePointer
          ? "Tap a visit to change its day or engineer."
          : "Drag a visit to another day or engineer to move it. Work in Needs scheduling can be dropped straight onto a cell to give it both."}
        {hasOrphans
          ? " A visit in the amber row is assigned to an engineer who is no longer listed: open it and pick a current engineer, or drag it into a row above. Until then it stays on the original engineer's phone."
          : ""}
      </p>
    </div>
  );
}

// One cell of the board: this engineer's (or the Unassigned row's) jobs on this day. A drop here
// calls drag.onDropBoardCell with BOTH coordinates; the hover highlight rides the shared overCell
// state under a composite "lane|day" key (see DragApi.onDropBoardCell).
function BoardCell({
  laneKey,
  engineerId,
  day,
  entries,
  engineerName,
  drag,
  todayKey,
}: {
  laneKey: string;
  engineerId: string | null;
  day: Date;
  entries: CalEntry[];
  engineerName: Map<string, string>;
  drag: DragApi;
  // Session ARA (S-10): threaded through to EntryCard's Overdue badge -- the board's own cells page
  // by week, so an earlier day in the SAME week as "today" is a real, visible past date here too.
  todayKey?: string;
}) {
  const k = keyOf(day);
  const cellKey = `${laneKey}|${k}`;
  const over = drag.overCell === cellKey;
  return (
    <div
      data-testid="board-cell"
      data-lane={laneKey}
      data-day={k}
      onDragOver={(e) => {
        e.preventDefault();
        drag.onCellOver(cellKey);
      }}
      onDragLeave={() => drag.onCellLeave(cellKey)}
      onDrop={(e) => {
        e.preventDefault();
        void drag.onDropBoardCell(engineerId, day);
      }}
      className={`min-h-[64px] space-y-1.5 border-l border-ele-purple-accent p-1.5 transition ${over ? "bg-ele-inset-strong ring-1 ring-inset ring-ele-green" : ""}`}
    >
      {entries.map((e) => (
        <EntryCard key={entryKey(e)} entry={e} engineerName={engineerName} drag={drag} dragHint="drag to another day or engineer" compact todayKey={todayKey} />
      ))}
    </div>
  );
}

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MONTH_MAX_CARDS = 3;

// Year view: a 12-month grid for the browsed year. Each cell shows the count of scheduled jobs
// in that month; clicking it opens that month in the month view.
function YearView({ year, monthCounts, onOpenMonth }: { year: number; monthCounts: number[]; onOpenMonth: (monthIndex: number) => void }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {MONTH_NAMES.map((name, i) => {
        const count = monthCounts[i] ?? 0;
        return (
          <button
            key={name}
            data-testid="year-month-cell"
            onClick={() => onOpenMonth(i)}
            className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-left transition hover:border-ele-green"
          >
            <div className="font-heading text-lg tracking-wide text-ele-strong">{name}</div>
            <div className="mt-0.5 font-menu max-md:text-xs text-[10px] tracking-wider text-ele-blue">{year}</div>
            <div data-testid="year-month-count" className="mt-2 font-heading text-2xl text-ele-green">{count}</div>
            <div className="max-md:text-xs text-[10px] tracking-wide text-ele-body">{count === 1 ? "job" : "jobs"}</div>
          </button>
        );
      })}
    </div>
  );
}

function MonthView({
  cursorMonth,
  days,
  entriesByDay,
  trainingByDay,
  projectSpans: spans,
  engineerName,
  todayKey,
  drag,
}: {
  cursorMonth: number;
  days: Date[];
  entriesByDay: Map<string, CalEntry[]>;
  // Session ARA (S-9): a separate map, deliberately never merged into entriesByDay -- see
  // TrainingSessionMarker's own note on why a training session is not a CalEntry. Rendered above the
  // MONTH_MAX_CARDS-capped job/project list and never counted toward its own "+N more" total: the cap
  // exists to keep a busy day cell small, and a training overlay is not dispatchable work competing
  // for that same space accounting.
  trainingByDay: Map<string, CalendarTrainingSession[]>;
  // Session ARA (S-13): see WeekView's own identical prop for the full reasoning.
  projectSpans: ProjectSpan[];
  engineerName: Map<string, string>;
  todayKey: string;
  drag: DragApi;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (k: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  return (
    <div data-hscroll className="overflow-x-auto rounded-ele border border-ele-purple-accent">
      <div className="min-w-[840px]">
        <div className="grid" style={{ gridTemplateColumns: "repeat(7, minmax(110px, 1fr))" }}>
          {WEEKDAY_LABELS.map((w) => (
            <div key={w} className="border-b border-l border-ele-purple-accent first:border-l-0 bg-ele-inset px-2 py-2 text-center font-menu text-xs uppercase tracking-wider text-ele-blue">
              {w}
            </div>
          ))}
        </div>
        <div className="grid" style={{ gridTemplateColumns: "repeat(7, minmax(110px, 1fr))" }}>
          {days.map((d) => {
            const k = keyOf(d);
            const inMonth = d.getUTCMonth() === cursorMonth;
            const isToday = k === todayKey;
            const dayEntries = entriesByDay.get(k) ?? [];
            const dayTraining = trainingByDay.get(k) ?? [];
            const daySpans = spansForDay(spans, d);
            const isOpen = expanded.has(k);
            const shown = isOpen ? dayEntries : dayEntries.slice(0, MONTH_MAX_CARDS);
            const more = dayEntries.length - shown.length;
            return (
              <div
                key={k}
                data-testid="month-cell"
                data-day={k}
                onDragOver={(e) => {
                  e.preventDefault();
                  drag.onCellOver(k);
                }}
                onDragLeave={() => drag.onCellLeave(k)}
                onDrop={(e) => {
                  e.preventDefault();
                  void drag.onDropDay(d);
                }}
                className={`min-h-[96px] border-b border-l border-ele-purple-accent first:border-l-0 p-1 ${inMonth ? "" : "opacity-50"} ${
                  isToday ? "ring-1 ring-inset ring-ele-green" : ""
                } ${drag.overCell === k ? "bg-ele-inset-strong" : ""}`}
              >
                <div className="mb-1 text-right max-md:text-xs text-[10px] font-menu text-ele-body">{d.getUTCDate()}</div>
                {daySpans.length > 0 && (
                  <div data-testid="project-span-row" className="mb-1 space-y-0.5">
                    {daySpans.map((s) => (
                      <ProjectSpanStrip key={`span:${s.id}`} span={s} bleed="month" />
                    ))}
                  </div>
                )}
                <div className="space-y-1">
                  {dayTraining.map((s) => (
                    <TrainingSessionMarker key={`training:${s.id}`} session={s} />
                  ))}
                  {shown.map((e) => (
                    <EntryCard key={entryKey(e)} entry={e} engineerName={engineerName} drag={drag} todayKey={todayKey} />
                  ))}
                  {more > 0 && (
                    <button onClick={() => toggle(k)} className="w-full rounded-ele border border-ele-purple-accent py-0.5 max-md:text-xs text-[10px] text-ele-blue hover:border-ele-green">
                      +{more} more
                    </button>
                  )}
                  {isOpen && dayEntries.length > MONTH_MAX_CARDS && (
                    <button onClick={() => toggle(k)} className="w-full max-md:text-xs text-[10px] text-ele-body hover:text-ele-strong">
                      Show less
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
